import type { PedidoDisponivelParaCampanha } from '@dv/contracts'
import type { EncargosDoRecebivel } from '@dv/contracts'
import type {
  EntradaDaExigenciaDeAutorizacao,
  EntradaDeAutorizacao,
  EntradaDeRetiradaDaAutorizacao,
  InstrucoesDeFaturamento,
  PreviaDaExigenciaDeAutorizacao,
  RespostaAguardandoAutorizacao,
  RespostaDaAutorizacao,
  RespostaDaExigenciaDeAutorizacao,
  RespostaDaRetiradaDaAutorizacao,
  RevisaoDaAutorizacao,
} from '@dv/contracts'
import type {
  AtivoIntangivel,
  EntradaDeAtivoIntangivel,
  RespostaDeAtivosIntangiveis,
} from '@dv/contracts'
import type {
  EntradaDeAdesaoDoCopilot,
  EntradaDePreferenciasDoCopilot,
  EstadoDoCopilot,
} from '@dv/contracts'
import { EVENTO_DE_PREFERENCIAS } from './evento-preferencias.ts'
import { EVENTO_DE_PERFIL } from './evento-perfil.ts'
/**
 * Os endpoints que as telas do passo 3.8 consomem (spec 03 §2).
 *
 * Uma função por rota, com o tipo do contrato — a tela nunca monta URL. É o que faz
 * um `rename` de rota quebrar o build em vez de virar 404 em produção.
 */
import type {
  EntradaDeConferenciaDeDesconto,
  ContextoDeRenegociacao,
  EntradaDeRenegociacao,
  AlcadaAdministrada,
  Capitulo,
  CelulaDaMatriz,
  Contato,
  EntradaDeAlcada,
  ContatoVinculado,
  ContextoDeEntidades,
  Empresa,
  Entidade,
  EntradaDeCapacidadeDeFornecedor,
  EntradaDeCapacidadeDeParceiro,
  EntradaDeEdicaoDeContato,
  EntradaDeEdicaoDoCadastroDeEmpresa,
  EntradaDeEdicaoDeEntidade,
  EntradaDoAtoDeRegime,
  AtoDeRegime,
  EntradaDeGrupoDeConsolidacao,
  EntradaDeItemDeCatalogo,
  EntradaDeNovoCadastroDeEmpresa,
  EntradaDeNovaEntidade,
  EntradaDeNovoContato,
  EntradaDeNovoNivel,
  EntradaDeNovoUsuario,
  EntradaDeEdicaoDePortalExterno,
  EntradaDeEmpresaDoPortal,
  EntradaDeMembroDoPortal,
  EntradaDePortalExterno,
  EntradaDePerfil,
  EntradaDeTrocaDeSenha,
  EntradaDeVinculo,
  EntradaDeVinculoEditado,
  EntradaDeEdicaoDeNegocio,
  EntradaDeGanhar,
  EntradaDeItem,
  EntradaDeMoverEstagio,
  EntradaDeNegocio,
  EntradaDeParcela,
  EntradaDeParte,
  EntradaDePartesEPerfil,
  EntradaDePerder,
  LinhaDeNegocio,
  Negocio,
  NegocioParaEdicao,
  RespostaDeParceirosElegiveis,
  ResumoDoFunil,
  RecorteDeNegocios,
  UsuarioAtribuivel,
  EntradaDeCancelamento,
  EntradaDeContaContabil,
  EdicaoDeContaContabil,
  ContaContabil,
  RespostaDoPlanoDeContas,
  RespostaDeEdicaoDeContaContabil,
  EntradaDeAssinatura,
  EntradaDeCancelamentoDeAssinatura,
  EntradaDeEdicaoDePedido,
  EntradaDeEncerramento,
  EntradaDeGerarPedido,
  EntradaDeItensDoPedido,
  EntradaDaPreviaDaClassificacao,
  EntradaDeCriacaoDePedido,
  EntradaDeRecebivel,
  EntradaDeClassificacaoDoRecebivel,
  EntradaDeProgramacaoDeFaturamento,
  ProgramacaoDeFaturamento,
  LinhaDePedido,
  Pedido,
  RespostaDaPreviaDaClassificacao,
  RespostaDaTrilha,
  Assinatura,
  GrupoDeConsolidacao,
  ItemDeCatalogo,
  LinhaDeContato,
  LinhaDeEmpresa,
  Nivel,
  Perfil,
  PerfilDeFaturamento,
  PerfilDePagamento,
  ResultadoDoCadastroDeEmpresa,
  RespostaDeCep,
  RespostaDeEdicao,
  RespostaDeEnrolamento,
  RespostaDeLogin,
  RespostaDeDefinicaoDeSenha,
  Usuario,
  PortalExterno,
  RespostaDeMembroDoPortal,
  SugestaoDeEmpresaDoPortal,
  UsuarioDoPortal,
  VinculoDeContato,
  Atingimento,
  Atividade,
  EntradaDeAtividade,
  EntradaDeConclusao,
  DetalheDoExecutivo,
  EntradaDeMeta,
  Meta as MetaComercial,
  Previsao,
  Anexo,
  Comentario,
  EntidadeComAnexo,
  EntidadeComentavel,
  AlocacaoDaTarefa,
  EntradaDeAlocacao,
  EntradaDeEdicaoDeTarefa,
  EntradaDeTarefa,
  EstagioDaTarefa,
  ResumoDaTarefa,
  Tarefa,
  EntradaDeComentario,
  UrlDeAnexo,
  ItemDaFila,
  PedidoSemAgenda,
  AguardandoDataDeFaturar,
  ItemProgramado,
  BaixaDoRecebivel,
  BaixaRegistrada,
  Recebivel,
  ItemContabilParaRecebivel,
  EntradaDeLiquidacao,
  ContaBancaria,
  PassoDaRegua,
  ContatoDeCobranca,
  SaldoDaConta,
  PontoDaCurva,
  RunwayDeCaixa,
  TransferenciaRegistrada,
  CamadaDeCaixa,
  AgregadosDaAgendaDeCaixa,
  LinhaDaAgendaDeCaixa,
  PendenciaFiscalDoCaixa,
  PrevisaoDeCaixa,
  PrevisaoVencidaDoCaixa,
  EntradaDePrevisaoDeCaixa,
  EntradaDeAlteracaoDaPrevisao,
  DetalheDaApuracao,
  EntradaDeFechamentoDaApuracao,
  EntradaDeProvisao,
  EntradaDeRegraFiscal,
  OpcoesDoCalendarioFiscal,
  RegraDoCalendarioFiscal,
  RespostaDasApuracoes,
  TributoFiscalDoContrato,
  LinhaDoCaixaExecutado,
  PontoDaCurvaProjetada,
  ResumoDaCurvaProjetada,
  EstadoDaIntegracao,
  DivergenciaSicoob,
  DivergenciaDeInvestimento,
  EntradaDeDecisaoDeInvestimento,
  EntradaDeResolucaoDeDivergencia,
  IrrfRetidoEmAplicacoes,
  ResultadoDaImportacaoDeMovimentos,
  MovimentoBancario,
  PixRecebidoPendente,
  EntradaDeMovimento,
  EntradaDeTransferencia,
  EntradaDeConciliacao,
  EntradaDeRegraDaConciliacao,
  ItemDaBandejaDeConciliacao,
  ContaAPagarSemelhante,
  CandidatoDaConciliacao,
  RegraDaConciliacao,
  Nf,
  EntradaDeNf,
  EntradaDeCancelamentoDeNf,
  EntradaDeRetornoDeClearance,
  EntradaDeRegraDeComissao,
  EntradaDePagamentoDeComissao,
  EntradaDeValidacaoDeComissao,
  ExtratoDeComissao,
  LinhaDeComissao,
  OpcoesDaRegraDeComissao,
  RegraDeComissao,
  RespostaDeParceirosDaRegra,
  Cockpit,
  RespostaDaBuscaGlobal,
  BadgeDeNotificacoes,
  EntradaDePreferenciaDeNotificacao,
  EntradaDeVerificacaoWhatsapp,
  EstadoDoWhatsapp,
  FiltrosDeNotificacoes,
  ListaDeNotificacoes,
  Notificacao,
  PreferenciaDeNotificacao,
  PreferenciasDeNotificacao,
  ContaPagar,
  EntradaDeCancelamentoDaContaPagar,
  EntradaDeContaPagar,
  EntradaDePagamentoDaContaPagar,
  EntradaDeRateioDaContaPagar,
  EntradaDeRegistroDeDocumento,
  LinhaDeContaPagar,
  OpcoesDeContaPagar,
  AgregadosDeContasPagar,
  EntradaDeEdicaoDeFornecedor,
  EntradaDeHabilitacaoDeFornecedor,
  Fornecedor,
  PerfilDaParte,
  RespostaDeFornecedores,
  RespostaDeFornecedoresDaDespesa,
  RespostaDePartesParaHabilitar,
  ResultadoDaHabilitacao,
  EntradaDeRejeicaoDeDesembolso,
  LinkDeAprovacaoResolvido,
  RespostaDaFilaDeAprovacoes,
  RespostaDoHistoricoDeAprovacoes,
  ResultadoDaDecisaoDeDesembolso,
  TipoDeAlvoDeAprovacao,
  EntradaDeLinhasDeOrcamento,
  EntradaDaPreviaDeAto,
  EntradaDoAtoDeOrcamento,
  PrevisaoDoAto,
  EntradaDeNovaVersaoDeOrcamento,
  EntradaDeNovoOrcamento,
  RespostaDasLinhasDeOrcamento,
  ApoioAsLinhasDoOrcamento,
  RespostaDosOrcamentos,
  RespostaDaDre,
  RespostaDaRentabilidade,
  RespostaDaVariancia,
  RespostaDosLancamentos,
  BuscaDeColaboradores,
  EntradaDeAditivoRh,
  EntradaDeVinculoDeEmpresaContratada,
  EntradaDeAjusteManualRh,
  EntradaDeContratoRh,
  EntradaDeDesligamentoEmLote,
  EntradaDeEdicaoDoColaborador,
  EntradaDeLiquidacaoRh,
  EntradaDeNovoColaboradorComContrato,
  EntradaDeRegistroDeDocumentoRh,
  EntradaDeReativacaoEmLote,
  RespostaDaFichaDoColaborador,
  RespostaDaListaDeColaboradores,
  RespostaDosContratosRh,
  RespostaDosPagamentosRh,
  KpisDaEmpresa,
} from '@dv/contracts'
import { baixar, enviarArquivo, pedir } from './cliente.ts'

// ------------------------------------------------------------------ sessão

export const entrar = (corpo: { email: string; senha: string; manterConectado: boolean }) =>
  pedir<RespostaDeLogin>('/auth/login', { metodo: 'POST', corpo })

export const sair = () => pedir<{ ok: true }>('/auth/logout', { metodo: 'POST' })

export const sessaoAtual = () => pedir<{ email: string | null }>('/auth/sessao')

export const esqueciSenha = (email: string) =>
  pedir<{ ok: true }>('/auth/esqueci-senha', { metodo: 'POST', corpo: { email } })

export const redefinirSenha = (corpo: { token: string; senha: string }) =>
  pedir<RespostaDeDefinicaoDeSenha>('/auth/redefinir-senha', { metodo: 'POST', corpo })

export const aceitarConvite = (corpo: { token: string; senha: string }) =>
  pedir<RespostaDeDefinicaoDeSenha>('/auth/aceitar-convite', { metodo: 'POST', corpo })

export const enrolarSegundoFator = (corpo: { email?: string; senha?: string }) =>
  pedir<RespostaDeEnrolamento>('/auth/2fa/enroll', { metodo: 'POST', corpo })

/*
  As duas etapas que ABREM sessão levam `manterConectado` — a caixa "Confiar neste
  dispositivo" foi marcada na view de login, e é aqui que a sessão finalmente nasce.

  Sem ele o campo simplesmente não saía do navegador: quem usa segundo fator marcava
  a caixa e continuava com cookie de navegador, sem nada na tela dizer por quê. O
  `2fa/enroll` fica de fora de propósito — ele não abre sessão nenhuma.
*/
export const confirmarSegundoFator = (corpo: {
  email?: string
  senha?: string
  codigo: string
  manterConectado?: boolean
}) => pedir<RespostaDeLogin>('/auth/2fa/confirmar', { metodo: 'POST', corpo })

export const verificarSegundoFator = (corpo: {
  email: string
  senha: string
  codigo: string
  manterConectado?: boolean
}) => pedir<RespostaDeLogin>('/auth/2fa/verify', { metodo: 'POST', corpo })

/*
 * Três funções saíram daqui, e a ausência é deliberada — a contagem de consumidores
 * do rito de auditoria as achou com ZERO chamadores, e função de cliente HTTP sem
 * chamador é dado morto ou ligação esquecida (DS-110). Estas eram do primeiro tipo:
 *
 * - `abrirEntidade` (`GET /entidades/:id`): a 10.2 lista e edita em linha, nunca abre
 *   uma entidade sozinha.
 * - `vinculosDoContato` (`GET /contatos/:id/vinculos`): a ficha do contato já recebe
 *   os vínculos EMBUTIDOS em `GET /contatos/:id` — buscar de novo seria uma segunda
 *   fonte para a mesma resposta.
 * - `excluirNegocio` (`DELETE /negocios/:id`): negócio que não vai adiante é marcado
 *   PERDIDO, com motivo (DS-34) — é o que preserva a informação. Nenhuma das telas
 *   3.5/3.6 oferece excluir, e a spec não pede.
 *
 * As três ROTAS continuam na API, porque a spec 03 §3 as declara. O que não fica é o
 * cliente sem consumidor, que é o que faz alguém pensar que a tela já usa.
 */

// ---------------------------------------------------------------- entidades

export const listarEntidades = () => pedir<{ data: Entidade[] }>('/entidades')
export const contextoDeEntidade = () => pedir<ContextoDeEntidades>('/entidades/contexto')

export const criarEntidade = (corpo: EntradaDeNovaEntidade) =>
  pedir<Entidade>('/entidades', { metodo: 'POST', corpo })

export const salvarEntidade = (id: string, corpo: EntradaDeEdicaoDeEntidade) =>
  pedir<Entidade>(`/entidades/${id}`, { metodo: 'PATCH', corpo })

/** D-179, DS-625: o regime fiscal muda só pelo ato, com fonte, motivo e trilha. */
export const registrarRegimeFiscal = (id: string, corpo: EntradaDoAtoDeRegime) =>
  pedir<Entidade>(`/entidades/${id}/regime-fiscal`, { metodo: 'POST', corpo })

export const historicoDoRegimeFiscal = (id: string, sinal?: AbortSignal) =>
  pedir<{ data: AtoDeRegime[] }>(`/entidades/${id}/regime-fiscal/historico`, sinal === undefined ? {} : { sinal })

export const listarGrupos = () => pedir<{ data: GrupoDeConsolidacao[] }>('/grupos-consolidacao')

export const criarGrupo = (corpo: EntradaDeGrupoDeConsolidacao) =>
  pedir<GrupoDeConsolidacao>('/grupos-consolidacao', { metodo: 'POST', corpo })

export const salvarGrupo = (id: string, corpo: EntradaDeGrupoDeConsolidacao) =>
  pedir<GrupoDeConsolidacao>(`/grupos-consolidacao/${id}`, { metodo: 'PUT', corpo })

export const excluirGrupo = (id: string) =>
  pedir<{ excluido: true }>(`/grupos-consolidacao/${id}`, { metodo: 'DELETE' })

// ------------------------------------------------------------------ níveis

export const listarNiveis = () => pedir<{ data: Nivel[] }>('/niveis')

export const criarNivel = (corpo: EntradaDeNovoNivel) => pedir<Nivel>('/niveis', { metodo: 'POST', corpo })

export const renomearNivel = (id: string, corpo: { nome?: string; descricao?: string | null }) =>
  pedir<Nivel>(`/niveis/${id}`, { metodo: 'PATCH', corpo })

export const salvarMatriz = (id: string, matriz: readonly CelulaDaMatriz[]) =>
  pedir<Nivel>(`/niveis/${id}/matriz`, { metodo: 'PUT', corpo: { matriz } })

// --------------------------------------------------------------- catálogos

export const listarCatalogo = (tipo: string, filtro: 'todos' | 'ativos' | 'inativos' = 'todos', comUso = false) =>
  pedir<{ data: ItemDeCatalogo[] }>(`/catalogos/${tipo}?filtro=${filtro}${comUso ? '&com_uso=1' : ''}`)

export const criarItem = (tipo: string, corpo: EntradaDeItemDeCatalogo) =>
  pedir<ItemDeCatalogo>(`/catalogos/${tipo}`, { metodo: 'POST', corpo })

export const salvarItem = (tipo: string, id: string, corpo: EntradaDeItemDeCatalogo) =>
  pedir<RespostaDeEdicao>(`/catalogos/${tipo}/${id}`, { metodo: 'PATCH', corpo })

export const excluirItem = (tipo: string, id: string) =>
  pedir<{ excluido: true }>(`/catalogos/${tipo}/${id}`, { metodo: 'DELETE' })

// --------------------------------------------- alçadas de desembolso (10.1)
//
// A leitura de ADMINISTRAÇÃO é outra rota que a `/alcadas` da 6.5, e de propósito: a
// fila lê a faixa ativa e já resolvida; aqui é preciso ver também a desativada e o
// alcance. A explicação inteira está no controlador.

export const alcadasParaAdministrar = () =>
  pedir<{ data: AlcadaAdministrada[] }>('/alcadas/administracao')

export const criarAlcada = (corpo: EntradaDeAlcada) =>
  pedir<AlcadaAdministrada>('/alcadas', { metodo: 'POST', corpo })

export const salvarAlcada = (id: string, corpo: EntradaDeAlcada) =>
  pedir<AlcadaAdministrada>(`/alcadas/${id}`, { metodo: 'PATCH', corpo })

export const excluirAlcada = (id: string) =>
  pedir<{ excluida: true }>(`/alcadas/${id}`, { metodo: 'DELETE' })

// ---------------------------------------------------------------- usuários

export const listarUsuarios = () => pedir<{ data: Usuario[] }>('/usuarios')

export const convidarUsuario = (corpo: EntradaDeNovoUsuario) =>
  pedir<{ usuario: Usuario; linkDeConvite: string }>('/usuarios', { metodo: 'POST', corpo })

export const salvarUsuario = (id: string, corpo: Partial<EntradaDeNovoUsuario>) =>
  pedir<Usuario>(`/usuarios/${id}`, { metodo: 'PATCH', corpo })

export const atoDeUsuario = (id: string, ato: string) =>
  pedir<unknown>(`/usuarios/${id}/${ato}`, { metodo: 'POST' })

export const listarUsuariosDoPortal = () => pedir<{ data: UsuarioDoPortal[] }>('/portal/usuarios')

/*
  Os portais externos (D-131 · DS-527). O acesso de fora é por PORTAL: o convite, a revogação e a
  reativação são de um MEMBRO (pessoa × portal), e as rotas antigas por vínculo saíram da API.
*/
export const listarPortaisExternos = () => pedir<{ data: PortalExterno[] }>('/portal/portais')

export const portalExterno = (id: string) => pedir<PortalExterno>(`/portal/portais/${id}`)

export const sugestoesDoPortal = (id: string) =>
  pedir<{ data: SugestaoDeEmpresaDoPortal[] }>(`/portal/portais/${id}/sugestoes`)

export const criarPortalExterno = (corpo: EntradaDePortalExterno) =>
  pedir<PortalExterno>('/portal/portais', { metodo: 'POST', corpo })

export const renomearPortalExterno = (id: string, corpo: EntradaDeEdicaoDePortalExterno) =>
  pedir<PortalExterno>(`/portal/portais/${id}`, { metodo: 'PATCH', corpo })

export const adicionarEmpresaAoPortal = (id: string, corpo: EntradaDeEmpresaDoPortal) =>
  pedir<PortalExterno>(`/portal/portais/${id}/empresas`, { metodo: 'POST', corpo })

export const removerEmpresaDoPortal = (id: string, empresaId: string) =>
  pedir<PortalExterno>(`/portal/portais/${id}/empresas/${empresaId}`, { metodo: 'DELETE' })

export const convidarParaOPortal = (id: string, corpo: EntradaDeMembroDoPortal) =>
  pedir<RespostaDeMembroDoPortal>(`/portal/portais/${id}/membros`, { metodo: 'POST', corpo })

export const revogarMembroDoPortal = (membroId: string) =>
  pedir<PortalExterno>(`/portal/membros/${membroId}/revogar`, { metodo: 'POST' })

export const reativarMembroDoPortal = (membroId: string) =>
  pedir<PortalExterno>(`/portal/membros/${membroId}/reativar`, { metodo: 'POST' })

export const arquivarPortalExterno = (id: string) =>
  pedir<PortalExterno>(`/portal/portais/${id}/arquivar`, { metodo: 'POST' })

// ------------------------------------------------------------------ perfil

export const meuPerfil = () => pedir<Perfil>('/me')

export async function salvarPerfil(corpo: EntradaDePerfil): Promise<Perfil> {
  const salvo = await pedir<Perfil>('/me', { metodo: 'PATCH', corpo })
  window.dispatchEvent(new CustomEvent(EVENTO_DE_PREFERENCIAS, { detail: salvo.preferencias }))
  window.dispatchEvent(new CustomEvent(EVENTO_DE_PERFIL, { detail: salvo }))
  return salvo
}

export const trocarMinhaSenha = (corpo: EntradaDeTrocaDeSenha) =>
  pedir<Perfil>('/me', { metodo: 'PATCH', corpo })

export const minhasPreferenciasDeNotificacao = (sinal?: AbortSignal) =>
  pedir<PreferenciasDeNotificacao>('/me/preferencias-notificacao', sinal === undefined ? {} : { sinal })

export const salvarMinhaPreferenciaDeNotificacao = (corpo: EntradaDePreferenciaDeNotificacao) =>
  pedir<PreferenciaDeNotificacao>('/me/preferencias-notificacao', { metodo: 'PUT', corpo })

// ------------------------------------------------------- WhatsApp (D-204, DS-699)

/** Onde a pessoa está na adesão ao WhatsApp: a situação, os quatro últimos dígitos e o texto do consentimento; nunca o número. */
export const meuWhatsapp = (sinal?: AbortSignal) =>
  pedir<EstadoDoWhatsapp>('/me/whatsapp', sinal === undefined ? {} : { sinal })

/*
  Os três atos da adesão não devolvem nada que a tela use: depois de cada um ela relê o
  `GET /me/whatsapp`, que é a única forma do estado (a validade do código, as datas, a
  situação). Assim a 2.3 não depende do corpo de resposta de cada ato para dizer onde a
  pessoa está.
*/

/** Pede o código de 6 dígitos, que sai pelo próprio WhatsApp (no máximo 3 por hora). */
export async function pedirCodigoDoWhatsapp(): Promise<void> {
  await pedir<unknown>('/me/whatsapp/codigo', { metodo: 'POST' })
}

/** Confere o código e grava o consentimento, com a versão do texto lido, no mesmo ato. */
export async function verificarWhatsapp(corpo: EntradaDeVerificacaoWhatsapp): Promise<void> {
  await pedir<unknown>('/me/whatsapp/verificar', { metodo: 'POST', corpo })
}

/** Revoga a autorização: o servidor desliga as preferências de WhatsApp da pessoa no mesmo ato. */
export async function revogarWhatsapp(): Promise<void> {
  await pedir<unknown>('/me/whatsapp/revogar', { metodo: 'POST' })
}

// ------------------------------------------------------- DV Copilot (D-210)

/** Onde a pessoa está na adesão ao DV Copilot: a situação, os quatro últimos dígitos e o texto do consentimento. */
export const meuCopilot = (sinal?: AbortSignal) =>
  pedir<EstadoDoCopilot>('/me/copilot', sinal === undefined ? {} : { sinal })

/*
  Como os do WhatsApp, os três atos não devolvem nada que a tela use: depois de cada um ela relê o
  `GET /me/copilot`, que é a única forma do estado.
*/

/** Adere ao DV Copilot com a autorização marcada e a versão do texto lido; exige o WhatsApp verificado. */
export async function ativarCopilot(corpo: EntradaDeAdesaoDoCopilot): Promise<void> {
  await pedir<unknown>('/me/copilot/ativar', { metodo: 'POST', corpo })
}

/** Desativa o DV Copilot (vale também com ele desligado no servidor); as notificações pelo WhatsApp não mudam. */
export async function desativarCopilot(): Promise<void> {
  await pedir<unknown>('/me/copilot/desativar', { metodo: 'POST' })
}

/** Liga ou desliga o resumo diário do DV Copilot; só com a adesão vigente. */
export async function definirResumoDoCopilot(corpo: EntradaDePreferenciasDoCopilot): Promise<void> {
  await pedir<unknown>('/me/copilot', { metodo: 'PATCH', corpo })
}

// -------------------------------------------------------------- notificações

export function listarNotificacoes(filtros: FiltrosDeNotificacoes, sinal?: AbortSignal): Promise<ListaDeNotificacoes> {
  const busca = new URLSearchParams({
    arquivadas: String(filtros.arquivadas),
    page: String(filtros.page),
    limit: String(filtros.limit),
  })
  if (filtros.lidas !== undefined) busca.set('lidas', String(filtros.lidas))
  if (filtros.categoria !== undefined) busca.set('categoria', filtros.categoria)
  if (filtros.periodoDe !== undefined) busca.set('periodo_de', filtros.periodoDe)
  if (filtros.periodoAte !== undefined) busca.set('periodo_ate', filtros.periodoAte)
  return pedir<ListaDeNotificacoes>(`/notificacoes?${busca.toString()}`, sinal === undefined ? {} : { sinal })
}

export const badgeDeNotificacoes = (sinal?: AbortSignal) =>
  pedir<BadgeDeNotificacoes>('/notificacoes/badge', sinal === undefined ? {} : { sinal })

export const lerNotificacao = (id: string) =>
  pedir<Notificacao>(`/notificacoes/${id}/ler`, { metodo: 'POST' })

export const arquivarNotificacao = (id: string) =>
  pedir<Notificacao>(`/notificacoes/${id}/arquivar`, { metodo: 'POST' })

export const excluirNotificacao = (id: string) =>
  pedir<{ excluida: true }>(`/notificacoes/${id}`, { metodo: 'DELETE' })

export const lerTodasAsNotificacoes = () =>
  pedir<{ lidas: number }>('/notificacoes/ler-todas', { metodo: 'POST' })

// -------------------------------------------------------- documentação e CEP

export const capitulos = () => pedir<{ capitulos: Capitulo[] }>('/docs/capitulos')

export const buscarCep = (cep: string) => pedir<RespostaDeCep>(`/cep/${cep}`)

// ------------------------------------------------------------------ cadastro

export interface FiltroDeEmpresas {
  readonly q?: string
  readonly setor?: string
  readonly capacidade?: string
  readonly tipoEmpresaId?: string
  /** País e Praça são múltiplos na gaveta da 3.1; viajam separados por vírgula. */
  readonly paises?: readonly string[]
  readonly pracaIds?: readonly string[]
  /** `nome` · `-nome` · `setor` · `-setor` — o cabeçalho da tabela da 3.1. */
  readonly sort?: string
  /**
   * A página do seletor com busca (item 03 · DS-529). Sem ele, a lista inteira: é o que a
   * 3.1 pede, porque pagina no cliente.
   */
  readonly limite?: number
  /** Ids que a resposta não traz (o Cliente na busca do Anunciante, a própria empresa). */
  readonly excluir?: readonly string[]
}

function comoQuery(filtro: FiltroDeEmpresas): string {
  const partes = new URLSearchParams()
  if (filtro.q !== undefined && filtro.q !== '') partes.set('q', filtro.q)
  if (filtro.setor !== undefined && filtro.setor !== '') partes.set('setor', filtro.setor)
  if (filtro.capacidade !== undefined && filtro.capacidade !== '') partes.set('capacidade', filtro.capacidade)
  if (filtro.tipoEmpresaId !== undefined && filtro.tipoEmpresaId !== '') {
    partes.set('tipo_empresa_id', filtro.tipoEmpresaId)
  }
  if (filtro.paises !== undefined && filtro.paises.length > 0) partes.set('pais', filtro.paises.join(','))
  if (filtro.pracaIds !== undefined && filtro.pracaIds.length > 0) {
    partes.set('praca_id', filtro.pracaIds.join(','))
  }
  if (filtro.sort !== undefined && filtro.sort !== '') partes.set('sort', filtro.sort)
  if (filtro.limite !== undefined) partes.set('limit', String(filtro.limite))
  if (filtro.excluir !== undefined && filtro.excluir.length > 0) partes.set('excluir', filtro.excluir.join(','))
  const texto = partes.toString()
  return texto === '' ? '' : `?${texto}`
}

/** As opções de `pedir` só com o sinal, quando houver: `exactOptionalPropertyTypes` não aceita `sinal: undefined`. */
function comSinal(sinal: AbortSignal | undefined): { sinal?: AbortSignal } {
  return sinal === undefined ? {} : { sinal }
}

export const empresas = (filtro: FiltroDeEmpresas = {}, sinal?: AbortSignal) =>
  pedir<{ dados: LinhaDeEmpresa[]; total: number }>(`/empresas${comoQuery(filtro)}`, comSinal(sinal))

export const empresa = (id: string, sinal?: AbortSignal) => pedir<Empresa>(`/empresas/${id}`, comSinal(sinal))

/**
 * Os quatro KPIs financeiros da ficha (3.2).
 *
 * O período governa três dos quatro. "Em aberto" e "A faturar" são posição e vêm iguais em
 * qualquer janela — quem decide isso é a API (spec 05 §3, exceção 2), não a tela.
 */
export const kpisDaEmpresa = (id: string, periodo: { de: string; ate: string }) =>
  pedir<KpisDaEmpresa>(`/empresas/${id}/kpis?periodo_de=${periodo.de}&periodo_ate=${periodo.ate}`)

/**
 * O Salvar da 3.3, num comando só (DS-593): a empresa e os blocos que a tela edita, numa
 * transação. Na criação, `corpo.id` é a intenção (o id que a empresa nova terá): repetir o
 * comando com ele devolve a empresa já gravada, com `recuperada: true`, sem criar outra.
 */
export const criarCadastroDeEmpresa = (corpo: EntradaDeNovoCadastroDeEmpresa) =>
  pedir<ResultadoDoCadastroDeEmpresa>('/empresas/cadastro', { metodo: 'POST', corpo })

export const salvarCadastroDeEmpresa = (id: string, corpo: EntradaDeEdicaoDoCadastroDeEmpresa) =>
  pedir<ResultadoDoCadastroDeEmpresa>(`/empresas/${id}/cadastro`, { metodo: 'PUT', corpo })

export const excluirEmpresa = (id: string) => pedir<{ ok: true }>(`/empresas/${id}`, { metodo: 'DELETE' })

/**
 * Desfazer a exclusão da empresa.
 *
 * O DELETE é soft (`deleted_at`), e é isto que faz o Desfazer valer também depois de a
 * página recarregar: quem desfaz devolve a MESMA linha, com o mesmo id, os mesmos
 * perfis e o mesmo histórico. Um desfazer que só existisse na memória do navegador
 * seria uma promessa que a primeira recarga quebra.
 */
export const restaurarEmpresa = (id: string) =>
  pedir<Empresa>(`/empresas/${id}/restaurar`, { metodo: 'POST' })

export const perfisDeFaturamento = (id: string) =>
  pedir<PerfilDeFaturamento[]>(`/empresas/${id}/perfis-faturamento`)

export const perfisDePagamento = (id: string) =>
  pedir<PerfilDePagamento[]>(`/empresas/${id}/perfis-pagamento`)

export const salvarCapacidadeDeParceiroDaEmpresa = (id: string, corpo: EntradaDeCapacidadeDeParceiro) =>
  pedir<Empresa>(`/empresas/${id}/capacidade-parceiro`, { metodo: 'PUT', corpo })

/**
 * A capacidade de fornecedor da EMPRESA — o par PJ de `salvarCapacidadeDeFornecedorDoContato`.
 *
 * A rota existe na API desde o cadastro e não tinha um só chamador: a ficha 3.2 exibia o
 * bloco e não sabia habilitá-lo. É a mesma forma da DS-110, no bloco que a própria DS-01
 * pediu.
 *
 * O corpo leva só o que a tela pergunta: desde a D-205/D-206 o servidor faz `coalesce` em
 * todos os termos (categoria, contato, observações e prazo), e chave ausente ou `null` mantém
 * o guardado. Quem quer trocar um termo manda o valor novo; `ativa` e `reativar` dizem o estado.
 */
export const salvarCapacidadeDeFornecedorDaEmpresa = (id: string, corpo: EntradaDeCapacidadeDeFornecedor) =>
  pedir<Empresa>(`/empresas/${id}/capacidade-fornecedor`, { metodo: 'PUT', corpo })

/** Quem trabalha nesta empresa — o outro lado de `vinculosDoContato` (D-39). */
export const contatosDaEmpresa = (id: string) =>
  pedir<ContatoVinculado[]>(`/empresas/${id}/contatos`)

export const contatos = (q?: string, opcoes: { readonly limite?: number; readonly sinal?: AbortSignal } = {}) => {
  const busca = new URLSearchParams()
  if (q !== undefined && q !== '') busca.set('q', q)
  // A página do seletor com busca (item 03 · DS-529); sem ela, a lista inteira da 3.4.
  if (opcoes.limite !== undefined) busca.set('limit', String(opcoes.limite))
  const texto = busca.toString()
  return pedir<{ dados: LinhaDeContato[]; total: number; totalSemFiltro: number }>(
    `/contatos${texto === '' ? '' : `?${texto}`}`,
    comSinal(opcoes.sinal),
  )
}

export const contato = (id: string) => pedir<Contato>(`/contatos/${id}`)

/**
 * Os aniversariantes da janela — a fonte do widget "Próximos aniversários" da 1.2.
 *
 * A rota existe desde o cadastro e estava sem chamador nenhum. A janela é do SERVIDOR
 * (dia-e-mês, virada de ano incluída, "hoje" vindo de fora do banco), e refazê-la aqui
 * pediria a lista inteira de contatos para descartar quase todos. `dias` é o parâmetro
 * que a API declara, e 7 é o default dela.
 */
export const aniversariantes = (dias = 7, sinal?: AbortSignal) =>
  pedir<LinhaDeContato[]>(`/contatos/aniversarios?dias=${String(dias)}`, sinal === undefined ? {} : { sinal })

export const criarContato = (corpo: EntradaDeNovoContato) =>
  pedir<Contato>('/contatos', { metodo: 'POST', corpo })

export const salvarContato = (id: string, corpo: EntradaDeEdicaoDeContato) =>
  pedir<Contato>(`/contatos/${id}`, { metodo: 'PATCH', corpo })

export const excluirContato = (id: string) => pedir<{ ok: true }>(`/contatos/${id}`, { metodo: 'DELETE' })

/**
 * Desfazer a exclusão do contato — o par de `excluirContato`.
 *
 * A exclusão é soft, então a pessoa continua no banco com o histórico inteiro; esta é
 * a chamada que o "Desfazer" do toast da 3.4 faz.
 */
export const restaurarContato = (id: string) =>
  pedir<Contato>(`/contatos/${id}/restaurar`, { metodo: 'POST' })

export const vincularContato = (id: string, corpo: EntradaDeVinculo) =>
  pedir<VinculoDeContato[]>(`/contatos/${id}/vinculos`, { metodo: 'POST', corpo })

/**
 * Edita um vínculo que já existe — hoje, o cargo.
 *
 * A rota nasceu com a auditoria de sessão 5: o modal da 3.4 gravava vínculos por
 * DIFERENÇA de `empresaId`, então trocar só o cargo não produzia chamada nenhuma e a
 * tela ainda dizia "Alterações salvas".
 */
export const atualizarVinculo = (id: string, empresaId: string, corpo: EntradaDeVinculoEditado) =>
  pedir<VinculoDeContato[]>(`/contatos/${id}/vinculos/${empresaId}`, { metodo: 'PATCH', corpo })

export const desvincularContato = (id: string, empresaId: string) =>
  pedir<VinculoDeContato[]>(`/contatos/${id}/vinculos/${empresaId}`, { metodo: 'DELETE' })

export const salvarCapacidadeDeParceiroDoContato = (id: string, corpo: EntradaDeCapacidadeDeParceiro) =>
  pedir<Contato>(`/contatos/${id}/capacidade-parceiro`, { metodo: 'PUT', corpo })

export const salvarCapacidadeDeFornecedorDoContato = (id: string, corpo: EntradaDeCapacidadeDeFornecedor) =>
  pedir<Contato>(`/contatos/${id}/capacidade-fornecedor`, { metodo: 'PUT', corpo })

export const bloquearCanal = (id: string, corpo: { canal: string; motivo: string }) =>
  pedir<Contato>(`/contatos/${id}/opt-out`, { metodo: 'POST', corpo })

export const liberarCanal = (id: string, canal: string) =>
  pedir<Contato>(`/contatos/${id}/opt-out/${canal}`, { metodo: 'DELETE' })

// ------------------------------------------------------------------- negócios

/**
 * Os filtros da 3.5 — os MESMOS para o quadro, a lista e os KPIs.
 *
 * Um objeto só, e não um parâmetro por chamada, porque a spec 3.5 manda os três
 * recalcularem juntos: KPI que responde a um filtro e coluna que responde a outro é
 * a forma mais direta de duas telas mostrarem números diferentes para a mesma coisa.
 */
export interface FiltroDeNegocios {
  /** Opt-in da 3.5 e do seu CSV: abertos sempre; fechados pelo período. */
  readonly recorte?: RecorteDeNegocios
  readonly periodoDe?: string
  readonly periodoAte?: string
  readonly estagioId?: string
  readonly responsavelId?: string
  readonly empresaId?: string
  /** A pessoa como PARTE do negócio (D-47) — diferente da empresa dela. */
  readonly contatoId?: string
  readonly parceiro?: string
  readonly pracaId?: string
  readonly setor?: string
  readonly q?: string
  readonly desfecho?: string
  readonly sort?: string
  /**
   * A SELEÇÃO da lista — o recorte que a barra em massa exporta.
   *
   * Lista vazia é ausência de recorte, e não "nenhum negócio": quem não marcou nada
   * exporta o filtro corrente, que é o que o botão do cabeçalho faz.
   */
  readonly ids?: readonly string[]
}

function queryDeNegocios(f: FiltroDeNegocios): string {
  const partes = new URLSearchParams()
  const por = (chave: string, valor: string | undefined): void => {
    if (valor !== undefined && valor !== '') partes.set(chave, valor)
  }
  por('periodo_de', f.periodoDe)
  por('periodo_ate', f.periodoAte)
  por('recorte', f.recorte)
  por('estagio_id', f.estagioId)
  por('responsavel_id', f.responsavelId)
  por('empresa_id', f.empresaId)
  por('contato_id', f.contatoId)
  por('parceiro', f.parceiro)
  por('praca_id', f.pracaId)
  por('setor', f.setor)
  por('q', f.q)
  por('desfecho', f.desfecho)
  por('sort', f.sort)
  por('ids', f.ids === undefined || f.ids.length === 0 ? undefined : f.ids.join(','))
  const texto = partes.toString()
  return texto === '' ? '' : `?${texto}`
}

export const negocios = (filtro: FiltroDeNegocios = {}) =>
  pedir<{ dados: LinhaDeNegocio[]; total: number }>(`/negocios${queryDeNegocios(filtro)}`)

export const funil = (eixo: 'estagio' | 'categoria_previsao', filtro: FiltroDeNegocios = {}) => {
  const query = queryDeNegocios(filtro)
  return pedir<ResumoDoFunil>(`/negocios/kanban${query === '' ? '?' : `${query}&`}eixo=${eixo}`)
}

export const negocio = (id: string) => pedir<Negocio>(`/negocios/${id}`)
export const negocioParaEdicao = (id: string) => pedir<NegocioParaEdicao>(`/negocios/${id}/edicao`)

export const criarNegocio = (corpo: EntradaDeNegocio) =>
  pedir<Negocio>('/negocios', { metodo: 'POST', corpo })

export const salvarNegocio = (id: string, corpo: EntradaDeEdicaoDeNegocio) =>
  pedir<Negocio>(`/negocios/${id}`, { metodo: 'PATCH', corpo })

export const salvarPartes = (id: string, corpo: readonly EntradaDeParte[]) =>
  pedir<Negocio>(`/negocios/${id}/partes`, { metodo: 'PUT', corpo })

/** Cliente, Anunciante, parceiros e perfil de faturamento num ato só (A-01, N-15). */
export const salvarPartesEPerfil = (id: string, corpo: EntradaDePartesEPerfil) =>
  pedir<Negocio>(`/negocios/${id}/partes-e-perfil`, { metodo: 'PUT', corpo })

export const salvarItens = (id: string, corpo: readonly EntradaDeItem[]) =>
  pedir<Negocio>(`/negocios/${id}/itens`, { metodo: 'PUT', corpo })

export const salvarParcelas = (id: string, corpo: readonly EntradaDeParcela[]) =>
  pedir<Negocio>(`/negocios/${id}/parcelas`, { metodo: 'PUT', corpo })

export const moverEstagio = (id: string, corpo: EntradaDeMoverEstagio) =>
  pedir<Negocio>(`/negocios/${id}/mover-estagio`, { metodo: 'POST', corpo })

export const ganharNegocio = (id: string, corpo: EntradaDeGanhar) =>
  pedir<Negocio>(`/negocios/${id}/ganhar`, { metodo: 'POST', corpo })

export const perderNegocio = (id: string, corpo: EntradaDePerder) =>
  pedir<Negocio>(`/negocios/${id}/perder`, { metodo: 'POST', corpo })

/**
 * A busca dos parceiros elegíveis (item 03 · DS-529).
 *
 * `chaves` são os ids que o pedido e o negócio gravam (empresa ou contato): é por eles que
 * o seletor resolve o nome de um parceiro que não está na página. Sem `limite`, a lista
 * inteira, como antes.
 */
export interface FiltroDeParceiros {
  readonly q?: string
  readonly limite?: number
  readonly chaves?: readonly string[]
}

function queryDeParceiros(filtro: FiltroDeParceiros): string {
  const busca = new URLSearchParams()
  if (filtro.q !== undefined && filtro.q !== '') busca.set('q', filtro.q)
  if (filtro.limite !== undefined) busca.set('limit', String(filtro.limite))
  if (filtro.chaves !== undefined && filtro.chaves.length > 0) busca.set('chaves', filtro.chaves.join(','))
  const texto = busca.toString()
  return texto === '' ? '' : `?${texto}`
}

export const parceirosParaEdicaoDeNegocio = (filtro: FiltroDeParceiros = {}, sinal?: AbortSignal) =>
  pedir<RespostaDeParceirosElegiveis>(`/parceiros/para-edicao${queryDeParceiros(filtro)}`, comSinal(sinal))

export const parceirosElegiveis = (filtro: FiltroDeParceiros = {}, sinal?: AbortSignal) =>
  pedir<RespostaDeParceirosElegiveis>(`/parceiros${queryDeParceiros(filtro)}`, comSinal(sinal))

export const usuariosAtribuiveis = () => pedir<{ data: UsuarioAtribuivel[]; meta: { ativos: number } }>('/usuarios/atribuiveis')

// -------------------------------------------------------------------- pedidos

/**
 * Os filtros da 4.1 — um objeto só, pelos mesmos motivos do funil: a tabela, a Σ do
 * rodapé e a contagem do subtítulo têm de responder ao MESMO recorte.
 */
export interface FiltroDePedidos {
  readonly q?: string
  readonly situacao?: string
  readonly setor?: string
  readonly responsavelId?: string
  readonly empresaId?: string
  readonly pracaId?: string
  readonly pracaIds?: readonly string[]
  readonly periodoDe?: string
  readonly periodoAte?: string
  readonly sort?: string
}

function queryDePedidos(f: FiltroDePedidos): string {
  const partes = new URLSearchParams()
  const por = (chave: string, valor: string | undefined): void => {
    if (valor !== undefined && valor !== '') partes.set(chave, valor)
  }
  por('q', f.q)
  por('situacao', f.situacao)
  por('setor', f.setor)
  por('responsavel_id', f.responsavelId)
  por('empresa_id', f.empresaId)
  por('praca_id', f.pracaIds === undefined ? f.pracaId : f.pracaIds.join(','))
  por('periodo_de', f.periodoDe)
  por('periodo_ate', f.periodoAte)
  por('sort', f.sort)
  const texto = partes.toString()
  return texto === '' ? '' : `?${texto}`
}

export const pedidos = (filtro: FiltroDePedidos = {}) =>
  pedir<{ dados: LinhaDePedido[]; total: number; totalDaCarteira: number; soma: string | null }>(`/pedidos${queryDePedidos(filtro)}`)

export const pedidosParaCampanha = (sinal:AbortSignal) =>
  pedir<{dados:PedidoDisponivelParaCampanha[];total:number;totalDaCarteira:number;soma:string|null}>('/pedidos?saldo_campanha=aberto',{sinal})

export const pedido = (id: string) => pedir<Pedido>(`/pedidos/${id}`)

/**
 * O pedido nasce INTEIRO, com as partes, numa transação só. A rota é a nova de propósito: na API
 * anterior ela não existe (404) e nada se grava, em vez de o `POST /pedidos` de sempre aceitar o
 * corpo e descartar as partes em silêncio.
 */
export const criarPedido = (corpo: EntradaDeCriacaoDePedido) =>
  pedir<Pedido>('/pedidos/completo', { metodo: 'POST', corpo })

export const salvarPedido = (id: string, corpo: EntradaDeEdicaoDePedido) =>
  pedir<Pedido>(`/pedidos/${id}`, { metodo: 'PATCH', corpo })

/** PK-01: a classificação da comissão que a gravação daria, conferida ANTES de gravar. */
export const previaDaClassificacao = (corpo: EntradaDaPreviaDaClassificacao) =>
  pedir<RespostaDaPreviaDaClassificacao>('/pedidos/classificacao-previa', { metodo: 'POST', corpo })

/**
 * O conjunto de itens, agora sempre em ENVELOPE (fase expand do D1/D2 · PED-02).
 *
 * O array cru continua valendo no contrato porque o binário N-1 o manda; o cliente
 * desta versão não o manda mais. Um compare-and-set que o cliente nunca exercita é
 * um compare-and-set que não existe: era o caso até aqui — o servidor sabia comparar
 * versão e o formulário nunca dizia qual tinha lido.
 */
export const salvarItensDoPedido = (id: string, corpo: EntradaDeItensDoPedido) =>
  pedir<Pedido>(`/pedidos/${id}/itens`, { metodo: 'PUT', corpo })

export const salvarRecebiveis = (id: string, corpo: readonly EntradaDeRecebivel[]) =>
  pedir<Pedido>(`/pedidos/${id}/recebiveis`, { metodo: 'PUT', corpo })

export const contextoParaRenegociar = (id:string) =>
  pedir<ContextoDeRenegociacao>(`/pedidos/${id}/renegociacao`)

export const renegociarPedido = (id:string,corpo:EntradaDeRenegociacao) =>
  pedir<Pedido>(`/pedidos/${id}/renegociacoes`,{metodo:'POST',corpo})

export const cancelarPedido = (id: string, corpo: EntradaDeCancelamento) =>
  pedir<Pedido>(`/pedidos/${id}/cancelar`, { metodo: 'POST', corpo })

export const encerrarPedido = (id: string, corpo: EntradaDeEncerramento) =>
  pedir<Pedido>(`/pedidos/${id}/encerrar`, { metodo: 'POST', corpo })

export const excluirPedido = (id: string) =>
  pedir<{ ok: true }>(`/pedidos/${id}`, { metodo: 'DELETE' })

export const assinaturas = (pedidoId: string) =>
  pedir<{ dados: Assinatura[]; mrrTotal: string | null }>(`/assinaturas?pedido_id=${encodeURIComponent(pedidoId)}`)

export const criarAssinatura = (corpo: EntradaDeAssinatura) =>
  pedir<Assinatura>('/assinaturas', { metodo: 'POST', corpo })

export const suspenderAssinatura = (id: string) =>
  pedir<Assinatura>(`/assinaturas/${id}/suspender`, { metodo: 'POST', corpo: {} })

export const reativarAssinatura = (id: string) =>
  pedir<Assinatura>(`/assinaturas/${id}/reativar`, { metodo: 'POST', corpo: {} })

export const cancelarAssinatura = (id: string, corpo: EntradaDeCancelamentoDeAssinatura) =>
  pedir<Assinatura>(`/assinaturas/${id}/cancelar`, { metodo: 'POST', corpo })

/**
 * O ato que transforma o negócio ganho em pedido.
 *
 * Fica aqui e não em `negocios` porque o que ele devolve é um PEDIDO — quem chama
 * está a caminho da 4.3, não de volta ao funil. O corpo leva o prazo e o perfil que a
 * revisão mostrou, e é com eles que o pedido nasce (DS-635).
 */
export const gerarPedidoDoNegocio = (negocioId: string, corpo: EntradaDeGerarPedido = {}) =>
  pedir<Pedido>(`/negocios/${negocioId}/gerar-pedido`, { metodo: 'POST', corpo })

// ------------------------------------------------------------- campanhas · 5.1/5.2

import type {
  Campanha,
  CatalogoDoReclassificar,
  EntradaDeCampanha,
  EntradaDeCancelamentoDeCampanha,
  EntradaDeEdicaoDeCampanha,
  EntradaDeEncerramentoDeVeiculacao,
  EntradaDeInicioDaCampanha,
  EntradaDePausaDaCampanha,
  EntradaDeReaberturaDaConclusao,
  EntradaDaComposicaoDaCampanha,
  EntradaDaFonteDoGasto,
  EntradaDeRealizadoOperacional,
  EntradaDeReclassificacaoDoItem,
  MargemDaCampanha,
  RealizadoOperacionalDaCampanha,
  RespostaDeCampanhas,
} from '@dv/contracts'

interface FiltrosDeCampanhas {
  pedidoId?: string
  status?: string
  periodoDe?: string
  periodoAte?: string
  produtoId?: string
  setor?: string
  pracaId?: string
  /** RAZÃO decimal, como o contrato transporta a margem: 0.25 é 25%. */
  margemMin?: string
  ordenar?: string
  /**
   * "Só acima do planejado" (§5.7, P-15, DS-654): `true` manda `acima_do_planejado=1`. Filtra no servidor, depois da
   * apuração, na lista, nos KPIs e no CSV; a tela não decide quem está acima.
   */
  acimaDoPlanejado?: boolean
}

function buscaDeCampanhas(f: FiltrosDeCampanhas): URLSearchParams {
  const b = new URLSearchParams()
  if (f.pedidoId !== undefined && f.pedidoId !== '') b.set('pedido_id', f.pedidoId)
  if (f.status !== undefined && f.status !== '') b.set('status', f.status)
  if (f.periodoDe !== undefined && f.periodoDe !== '') b.set('periodo_de', f.periodoDe)
  if (f.periodoAte !== undefined && f.periodoAte !== '') b.set('periodo_ate', f.periodoAte)
  if (f.produtoId !== undefined && f.produtoId !== '') b.set('produto_id', f.produtoId)
  if (f.setor !== undefined && f.setor !== '') b.set('setor', f.setor)
  if (f.pracaId !== undefined && f.pracaId !== '') b.set('praca_id', f.pracaId)
  if (f.margemMin !== undefined && f.margemMin !== '') b.set('margem_min', f.margemMin)
  if (f.ordenar !== undefined && f.ordenar !== '') b.set('ordenar', f.ordenar)
  if (f.acimaDoPlanejado === true) b.set('acima_do_planejado', '1')
  return b
}

export const campanhas = (f: FiltrosDeCampanhas = {}, sinal?: AbortSignal) => {
  const busca = buscaDeCampanhas(f).toString()
  return pedir<RespostaDeCampanhas>(
    busca === '' ? '/campanhas' : `/campanhas?${busca}`,
    sinal === undefined ? {} : { sinal },
  )
}

/**
 * Exporta o RECORTE da lista, com os mesmos filtros que a tela está mostrando.
 *
 * Reaproveita o montador de busca de `campanhas` de propósito: um segundo lugar montando a
 * mesma URL seria o jeito mais silencioso de o arquivo sair com um recorte diferente do da
 * página — e ninguém compara os dois até o dia em que alguém compara.
 */
export const exportarCampanhas = (f: Parameters<typeof campanhas>[0] = {}) => {
  const busca = buscaDeCampanhas(f)
  busca.set('formato', 'csv')
  return baixar(`/campanhas/exportar?${busca.toString()}`)
}

export const campanhaPorId = (id: string, sinal?: AbortSignal) =>
  pedir<Campanha>(`/campanhas/${id}`, sinal === undefined ? {} : { sinal })

export const margemDaCampanha = (id: string, sinal?: AbortSignal) =>
  pedir<MargemDaCampanha>(`/campanhas/${id}/margem`, sinal === undefined ? {} : { sinal })

/** PK-12 (D-128): o realizado OPERACIONAL e o realizado em CAP das contas de mídia. */
export const realizadoOperacionalDaCampanha = (id: string, sinal?: AbortSignal) =>
  pedir<RealizadoOperacionalDaCampanha>(`/campanhas/${id}/realizado-operacional`, sinal === undefined ? {} : { sinal })

/** Um apontamento novo — o registro é append-only, e o anterior fica. */
export const registrarRealizadoOperacional = (id: string, corpo: EntradaDeRealizadoOperacional) =>
  pedir<RealizadoOperacionalDaCampanha>(`/campanhas/${id}/realizado-operacional`, { metodo: 'POST', corpo })

/**
 * Fonte do gasto, o ato da campanha inteira (F-5, P-18, DS-653): o escopo de cada item e a fonte única do grupo
 * coberto, numa transação; devolve a mesma leitura do gasto. Um consumidor: `ModalDeFonteDoGasto` (5.2).
 */
export const definirFonteDoGasto = (id: string, corpo: EntradaDaFonteDoGasto) =>
  pedir<RealizadoOperacionalDaCampanha>(`/campanhas/${id}/fonte-do-gasto`, { metodo: 'PUT', corpo })

export const criarCampanha = (corpo: EntradaDeCampanha) =>
  pedir<Campanha>('/campanhas', { metodo: 'POST', corpo })

export const editarCampanha = (id: string, corpo: EntradaDeEdicaoDeCampanha) =>
  pedir<Campanha>(`/campanhas/${id}`, { metodo: 'PATCH', corpo })

/**
 * A composição (PK-07) por identidade (D-190, P-21, DS-646): os itens, os outros custos gerais e a
 * VERSÃO da composição que a tela leu, num ato só.
 *
 * A versão é obrigatória AQUI, embora o contrato a aceite ausente: ausente é a tela anterior, que o
 * servidor só deixa passar na campanha sem itens e recusa com 409 `campanha.composicao_sem_versao` na
 * campanha com itens. Esta tela sempre sabe a versão (ela vem na leitura da campanha), então o tipo
 * cobra que ela vá no fio. Divergente é 409 `campanha.composicao_conflito`, e a tela pede para atualizar.
 */
export const salvarItensDaCampanha = (
  id: string,
  corpo: EntradaDaComposicaoDaCampanha & { readonly versaoComposicao: number },
) => pedir<Campanha>(`/campanhas/${id}/itens`, { metodo: 'PUT', corpo })

/**
 * Reclassificar UM item da campanha (D-190, P-22 a P-25, DS-647): grava na hora, com trilha, e devolve
 * a campanha como o servidor a leu depois do ato. `itemId` é o id do item DA CAMPANHA, não o do pedido.
 */
export const reclassificarItemDaCampanha = (id: string, itemId: string, corpo: EntradaDeReclassificacaoDoItem) =>
  pedir<Campanha>(`/campanhas/${id}/itens/${itemId}/reclassificar`, { metodo: 'POST', corpo })

export const alterarValorDaCampanha = (id: string, novoValor: string) =>
  pedir<Campanha>(`/campanhas/${id}/valor`, { metodo: 'POST', corpo: { novoValor } })

/*
  Os atos de status (D-189, §5.4, DS-656): cada um uma rota, uma transação no servidor, e a ficha como ele a leu depois
  do ato (201). O Concluir continua na rota de sempre (`encerrar-veiculacao`), e o Cancelar também. Um consumidor cada:
  os diálogos do menu "Alterar status" da 5.2 (`campanha-blocos.tsx`).
*/

/** Iniciar (P-03): `{}` com o início planejado alcançado; `{ inicioEfetivo }` quando o servidor o pede. */
export const iniciarCampanha = (id: string, corpo: EntradaDeInicioDaCampanha) =>
  pedir<Campanha>(`/campanhas/${id}/iniciar`, { metodo: 'POST', corpo })

/** Pausar (§5.4): o motivo é opcional. Não pausa nada no DV360. */
export const pausarCampanha = (id: string, corpo: EntradaDePausaDaCampanha) =>
  pedir<Campanha>(`/campanhas/${id}/pausar`, { metodo: 'POST', corpo })

/** Retomar (§5.4): Pausada volta a Em andamento, sem campos. */
export const retomarCampanha = (id: string) =>
  pedir<Campanha>(`/campanhas/${id}/retomar`, { metodo: 'POST', corpo: {} })

/** Concluir (P-04): a data efetiva, entre o início e hoje, na rota de sempre. */
export const encerrarVeiculacaoDaCampanha = (id: string, corpo: EntradaDeEncerramentoDeVeiculacao) =>
  pedir<Campanha>(`/campanhas/${id}/encerrar-veiculacao`, { metodo: 'POST', corpo })

export const cancelarCampanha = (id: string, corpo: EntradaDeCancelamentoDeCampanha) =>
  pedir<Campanha>(`/campanhas/${id}/cancelar`, { metodo: 'POST', corpo })

/** Reabrir conclusão (P-06): o motivo, de 10 a 500 caracteres; `midia · editar_todos`. */
export const reabrirConclusaoDaCampanha = (id: string, corpo: EntradaDeReaberturaDaConclusao) =>
  pedir<Campanha>(`/campanhas/${id}/reabrir-conclusao`, { metodo: 'POST', corpo })

/**
 * O catálogo do Reclassificar SOB a campanha (DS-657 (d); E3T-M8): com a permissão de quem edita a campanha, e não a de
 * ver o catálogo. Só produtos ativos vendidos com veiculação; o percentual da meta padrão, só para quem edita os custos.
 * Um consumidor: `ModalDeReclassificacao` (5.2).
 */
export const catalogoDoReclassificar = (id: string, sinal?: AbortSignal) =>
  pedir<CatalogoDoReclassificar>(`/campanhas/${id}/reclassificacao/catalogo`, sinal === undefined ? {} : { sinal })

/**
 * Arquivar — o `DELETE` que a lista 5.1 chama pela seleção em massa.
 *
 * No servidor o verbo se chama `excluir` e é SOFT delete (`deleted_at`): a campanha sai das
 * listas e do recorte dos KPIs, e nada é apagado do banco. A marca de permissão é `excluir`
 * no módulo `midia`, a mesma que cada linha da lista já devolve em `capacidades.podeCancelar`
 * — por isso a caixa de seleção só aparece onde ela é verdadeira, e nenhum clique termina em
 * 403 surpresa.
 *
 * Não existe volta: campanha não tem `POST /campanhas/:id/restaurar` como anexo, comentário e
 * conta a pagar têm. Quem chama pergunta ANTES e não oferece Desfazer.
 */
export const arquivarCampanha = (id: string) =>
  pedir<{ ok: true }>(`/campanhas/${id}`, { metodo: 'DELETE' })

// ------------------------------------------------------- links da campanha · 5.2 (P-51)

import type {
  EdicaoDeLinkDaCampanha,
  EntradaDeLinkDaCampanha,
  LinkDaCampanha,
  LinksDaCampanha,
} from '@dv/contracts'

/*
  Os links da campanha (P-51, DS-660), as cinco rotas que a seção Links da 5.2 usa. O ENDEREÇO do link só viaja no
  corpo JSON (criar e editar): os caminhos levam só os ids, e nada aqui monta query string. Um consumidor cada: a seção
  Links da ficha (`SecaoDeLinks`, em `rotas/campanha-blocos.tsx`). `GET /campanhas/:id/links/:linkId` existe e a tela
  não precisa dele: a lista já traz cada link inteiro.
*/

/** Os ativos que a pessoa vê (o restrito só para quem vê custos), mais novo primeiro, com o limite e as capacidades. */
export const listarLinksDaCampanha = (id: string, sinal?: AbortSignal) =>
  pedir<LinksDaCampanha>(`/campanhas/${id}/links`, sinal === undefined ? {} : { sinal })

/** Adicionar link (201): o título, o endereço e a marca de restrito, no corpo. */
export const criarLinkDaCampanha = (id: string, corpo: EntradaDeLinkDaCampanha) =>
  pedir<LinkDaCampanha>(`/campanhas/${id}/links`, { metodo: 'POST', corpo })

/** Editar link: só o que mudou vai no corpo. */
export const editarLinkDaCampanha = (id: string, linkId: string, corpo: EdicaoDeLinkDaCampanha) =>
  pedir<LinkDaCampanha>(`/campanhas/${id}/links/${linkId}`, { metodo: 'PATCH', corpo })

/** Excluir link: exclusão lógica (o link sai da lista e a linha fica no banco). */
export const excluirLinkDaCampanha = (id: string, linkId: string) =>
  pedir<{ ok: true }>(`/campanhas/${id}/links/${linkId}`, { metodo: 'DELETE' })

/** O Desfazer do aviso de exclusão (201): só quem excluiu, até 15 minutos depois. */
export const restaurarLinkDaCampanha = (id: string, linkId: string) =>
  pedir<LinkDaCampanha>(`/campanhas/${id}/links/${linkId}/restaurar`, { metodo: 'POST' })

/**
 * Os motivos do Cancelar SOB a campanha (E5T-R1, DS-670): o guarda é o do próprio ato (quem edita esta campanha com o
 * marcador `excluir`), sem pedir para ver os Catálogos; só os motivos ATIVOS do contexto do cancelamento. Um consumidor:
 * `ModalDoCancelamento` (5.2).
 */
export const motivosDoCancelamentoDaCampanha = (id: string, sinal?: AbortSignal) =>
  pedir<MotivosDoCancelamentoDaCampanha>(`/campanhas/${id}/cancelamento/motivos`, comSinal(sinal))

// ------------------------------------------------------- DV360 · 5.2 (P-33, P-58; D-192, DS-684)

import type {
  CampaignsDv360Disponiveis,
  EntradaDaDescobertaDv360,
  EntradaDeDesvinculoDv360,
  EntradaDeVinculoDv360,
  MotivosDoCancelamentoDaCampanha,
  VinculosDv360DaCampanha,
  AmpliacaoDeHistoricoDv360,
  CriativosDv360DoEscopo,
  CriativosDv360DoLineItem,
  EntradaDeAmpliacaoDeHistoricoDv360,
  EntradaDeEncerramentoDeColetaDv360,
  InsertionOrdersDv360DaCampaign,
  LineItemsDv360DoInsertionOrder,
  MidiaDv360DaCampanha,
  RetomadaDoAcompanhamentoDv360,
  SerieDv360,
  SincronizacoesDv360DaCampanha,
} from '@dv/contracts'

/*
  O vínculo campanha ↔ Campaigns DV360, sob a campanha (DS-668, D-192, DS-684): as MESMAS rotas para a ficha e para a
  Nova campanha (P-58). Nenhuma chama o Google dentro da requisição: a lista pesquisável lê o catálogo do Partner que a
  API mantém em memória, e vincular confere contra ele e nasce "aguardando conferência". Consumidores: a seção
  `SecaoMidiaDv360` e o diálogo `ModalDeVinculoDv360` (5.2).
*/

/** Os vínculos não anulados da campanha (`midia · ver`): IDs, estado e a parte comercial conferida no ato, e as capacidades. */
export const vinculosDv360DaCampanha = (id: string, sinal?: AbortSignal) =>
  pedir<VinculosDv360DaCampanha>(`/campanhas/${id}/dv360/vinculos`, comSinal(sinal))

/**
 * A lista pesquisável do diálogo Vincular (DS-684 (a) a (d)): uma página de 50 do catálogo do Partner, com o estado dele
 * ("k de n" Advertisers, a hora da lista e a falha), o contexto do passo Conferir e a situação de cada linha para quem
 * pede. Sempre 200 depois das guardas, em todo estado do catálogo; nada é gravado. `atualizar` é o "Atualizar lista"
 * (força um ciclo, no máximo a cada 60 s). Os parâmetros vão no fim do caminho, como as leituras do bloco, para a guarda
 * `rotas-do-front` ler o caminho.
 */
export const campaignsDv360Disponiveis = (
  id: string,
  filtros: { readonly q: string; readonly status: NonNullable<EntradaDaDescobertaDv360['status']>; readonly pagina: number; readonly atualizar: boolean },
  sinal?: AbortSignal,
) => {
  const busca = new URLSearchParams()
  if (filtros.q.trim() !== '') busca.set('q', filtros.q.trim())
  busca.set('status', filtros.status)
  busca.set('pagina', String(filtros.pagina))
  if (filtros.atualizar) busca.set('atualizar', '1')
  const consulta = busca.toString()
  return pedir<CampaignsDv360Disponiveis>(`/campanhas/${id}/dv360/campaigns-disponiveis${consulta === '' ? '' : `?${consulta}`}`, comSinal(sinal))
}

/**
 * Vincular (201), tudo ou nada: só os IDs confirmados no passo Conferir (`{campaignIds}`, de 1 a 20); o Advertiser, o
 * nome, a moeda e o fuso vêm do catálogo do servidor. Os vínculos nascem aguardando conferência; a resposta traz a lista e
 * os avisos.
 */
export const vincularCampaignsDv360 = (id: string, corpo: EntradaDeVinculoDv360) =>
  pedir<VinculosDv360DaCampanha>(`/campanhas/${id}/dv360/vinculos`, { metodo: 'POST', corpo })

/** Desvincular (201), com o motivo: o único ato de saída, em qualquer status, com a integração ligada ou não. */
export const desvincularCampaignDv360 = (id: string, vinculoId: string, corpo: EntradaDeDesvinculoDv360) =>
  pedir<VinculosDv360DaCampanha>(`/campanhas/${id}/dv360/vinculos/${vinculoId}/desvincular`, { metodo: 'POST', corpo })

/*
  A LEITURA da seção "Mídia · DV360" (P-52, DS-672): seis rotas sob a campanha, com `midia · ver`; NENHUMA chama o
  Google (tudo vem do que a ingestão gravou). O recorte vai como `de` e `ate` juntos, ou nenhum (o Acumulado). Os custos
  e os nomes seguem a P-20 no servidor: a tela escreve o que vier. Consumidores: `SecaoMidiaDv360` (o bloco) e os
  componentes de `campanha-dv360-leitura.tsx` (IO → line item, série e criativos), 5.2.
*/

/** O recorte de análise na consulta: os dois juntos, ou nenhum. */
function comRecorte(busca: URLSearchParams, recorte: { readonly inicio: string; readonly fim: string } | null): URLSearchParams {
  if (recorte !== null) {
    busca.set('de', recorte.inicio)
    busca.set('ate', recorte.fim)
  }
  return busca
}

/*
  O caminho de cada leitura é um template literal com a consulta interpolada no FIM (`…${consulta === '' ? '' :
  `?${consulta}`}`), o idioma de `/contas-pagar` e `/metas/atingimento`: é o primeiro argumento de `pedir(` que a guarda
  `packages/config/test/rotas-do-front.test.js` lê e cruza com as rotas da API. Um embrulho (`comConsulta(caminho,
  busca)`) deixava a cauda dele no caminho lido, e as cinco chamadas viravam "tela morta" para a guarda. Bloco, IO, LI e
  série pedem `leitura=composta`; sem recorte, só `de`/`ate` ficam ausentes. O fio é provado em `dv360-leitura-no-fio.test.ts`.
*/

/** O bloco inteiro: resumo, alertas, fim delimitado, Campaigns e as capacidades dos atos. */
export const midiaDv360DaCampanha = (id: string, recorte: { readonly inicio: string; readonly fim: string } | null, sinal?: AbortSignal) => {
  const consulta = comRecorte(new URLSearchParams({ leitura: 'composta' }), recorte).toString()
  return pedir<MidiaDv360DaCampanha>(`/campanhas/${id}/dv360${consulta === '' ? '' : `?${consulta}`}`, comSinal(sinal))
}

/** Os IOs de uma Campaign confirmada, com os totais no recorte. */
export const iosDv360DaCampaign = (id: string, campaignId: string, recorte: { readonly inicio: string; readonly fim: string } | null, sinal?: AbortSignal) => {
  const consulta = comRecorte(new URLSearchParams({ leitura: 'composta' }), recorte).toString()
  return pedir<InsertionOrdersDv360DaCampaign>(`/campanhas/${id}/dv360/campaigns/${campaignId}/ios${consulta === '' ? '' : `?${consulta}`}`, comSinal(sinal))
}

/** Os line items de um IO, 50 por página, com a busca, o tipo, o status e a atividade. */
export const lineItemsDv360DoIo = (
  id: string,
  ioId: string,
  filtro: {
    readonly pagina: number
    readonly busca?: string | undefined
    readonly tipo?: string | undefined
    readonly status?: string | undefined
    readonly atividade?: 'com' | 'sem' | undefined
    readonly recorte: { readonly inicio: string; readonly fim: string } | null
  },
  sinal?: AbortSignal,
) => {
  const busca = new URLSearchParams({ pagina: String(filtro.pagina), leitura: 'composta' })
  if (filtro.busca !== undefined && filtro.busca !== '') busca.set('busca', filtro.busca)
  if (filtro.tipo !== undefined && filtro.tipo !== '') busca.set('tipo', filtro.tipo)
  if (filtro.status !== undefined && filtro.status !== '') busca.set('status', filtro.status)
  if (filtro.atividade !== undefined) busca.set('atividade', filtro.atividade)
  const consulta = comRecorte(busca, filtro.recorte).toString()
  return pedir<LineItemsDv360DoInsertionOrder>(`/campanhas/${id}/dv360/ios/${ioId}/line-items${consulta === '' ? '' : `?${consulta}`}`, comSinal(sinal))
}

/** A série por período: o grão, o escopo (`campanha`, `campaign:<id>`, `io:<id>`, `li:<id>`) e o recorte. */
export const serieDv360DaCampanha = (
  id: string,
  filtro: { readonly grao: 'dia' | 'semana' | 'mes'; readonly escopo: string; readonly recorte: { readonly inicio: string; readonly fim: string } | null },
  sinal?: AbortSignal,
) => {
  const consulta = comRecorte(new URLSearchParams({ grao: filtro.grao, escopo: filtro.escopo, leitura: 'composta' }), filtro.recorte).toString()
  return pedir<SerieDv360>(`/campanhas/${id}/dv360/serie${consulta === '' ? '' : `?${consulta}`}`, comSinal(sinal))
}

/** Os criativos de um line item (P-53): sem métrica. */
export const criativosDv360DoLineItem = (id: string, lineItemId: string, sinal?: AbortSignal) =>
  pedir<CriativosDv360DoLineItem>(`/campanhas/${id}/dv360/line-items/${lineItemId}/criativos`, comSinal(sinal))

/**
 * Os criativos distintos do escopo (as Campaigns confirmadas), 50 por página, com a busca e os filtros da seção (parte E
 * da E7b servidor, VT2-PC-08, DS-672 (s)): a Campaign, o IO, o tipo e o status no DV360 do LINE ITEM, os mesmos valores
 * da rota dos line items; o "usado em n" vem contado só entre os line items do recorte. O filtro vazio não vai ao fio (o
 * servidor o lê como "sem filtro").
 */
export const criativosDv360DaCampanha = (
  id: string,
  filtro: {
    readonly pagina: number
    readonly busca?: string | undefined
    readonly campaign?: string | undefined
    readonly io?: string | undefined
    readonly tipo?: string | undefined
    readonly status?: string | undefined
  },
  sinal?: AbortSignal,
) => {
  const busca = new URLSearchParams({ pagina: String(filtro.pagina) })
  if (filtro.busca !== undefined && filtro.busca !== '') busca.set('busca', filtro.busca)
  if (filtro.campaign !== undefined && filtro.campaign !== '') busca.set('campaign', filtro.campaign)
  if (filtro.io !== undefined && filtro.io !== '') busca.set('io', filtro.io)
  if (filtro.tipo !== undefined && filtro.tipo !== '') busca.set('tipo', filtro.tipo)
  if (filtro.status !== undefined && filtro.status !== '') busca.set('status', filtro.status)
  const consulta = busca.toString()
  return pedir<CriativosDv360DoEscopo>(`/campanhas/${id}/dv360/criativos${consulta === '' ? '' : `?${consulta}`}`, comSinal(sinal))
}

/*
  Os atos manuais da P-41 (DS-671), sob a campanha: NENHUM chama o Google na requisição; abrem ou mudam o que a
  varredura faz. Consumidores: a seção `SecaoMidiaDv360` e os diálogos de `campanha-dv360-leitura.tsx` (5.2).
*/

/** "Sincronizar agora" (quem edita a campanha): todas as Campaigns; as que ficaram de fora vêm nomeadas (D-199). */
export const sincronizarDv360 = (id: string) =>
  pedir<SincronizacoesDv360DaCampanha>(`/campanhas/${id}/dv360/sincronizar`, { metodo: 'POST', corpo: {} })

/** "Retomar acompanhamento" (quem edita os custos): o fim delimitado vai a hoje + 92. */
export const retomarAcompanhamentoDv360 = (id: string) =>
  pedir<RetomadaDoAcompanhamentoDv360>(`/campanhas/${id}/dv360/retomar-acompanhamento`, { metodo: 'POST', corpo: {} })

/** "Reconferir": enfileira a conferência do vínculo. */
export const reconferirVinculoDv360 = (id: string, vinculoId: string) =>
  pedir<SincronizacoesDv360DaCampanha>(`/campanhas/${id}/dv360/vinculos/${vinculoId}/reconferir`, { metodo: 'POST', corpo: {} })

/** "Encerrar coleta" (P-34): só a Campaign confirmada que passou a dar 404, com o motivo. */
export const encerrarColetaDv360 = (id: string, vinculoId: string, corpo: EntradaDeEncerramentoDeColetaDv360) =>
  pedir<VinculosDv360DaCampanha>(`/campanhas/${id}/dv360/vinculos/${vinculoId}/encerrar-coleta`, { metodo: 'POST', corpo })

/** "Ampliar histórico": o novo início da carga, anterior ao atual e nunca antes de D − 24 meses. */
export const ampliarHistoricoDv360 = (id: string, vinculoId: string, corpo: EntradaDeAmpliacaoDeHistoricoDv360) =>
  pedir<AmpliacaoDeHistoricoDv360>(`/campanhas/${id}/dv360/vinculos/${vinculoId}/ampliar-historico`, { metodo: 'POST', corpo })

/** "Tentar de novo": a Campaign suspensa por limite, validação ou desenho volta a tentar. */
export const tentarDeNovoDv360 = (id: string, vinculoId: string) =>
  pedir<SincronizacoesDv360DaCampanha>(`/campanhas/${id}/dv360/vinculos/${vinculoId}/tentar-de-novo`, { metodo: 'POST', corpo: {} })

// ------------------------------------------------------- Portal da 5.2 (P-46 a P-48, §5.3, §5.8; DS-675)

import type { PortalDaCampanha, PublicoDoPortal } from '@dv/contracts'

/*
  Os três interruptores da publicação no portal, sob a campanha (E8): ler com `midia · ver`, ligar e desligar com
  `midia · editar_todos` (as capacidades vêm na resposta, por público). Um público por chamada; ligar e desligar devolvem
  a mesma leitura. Nenhuma fila é prometida: desligar vale na próxima consulta do portal, e ligar aparece na
  materialização seguinte. Consumidor: a seção `SecaoDoPortal` e os dois diálogos dela (`rotas/campanha-portal.tsx`).
*/

/** O diálogo "Portal": os três públicos com a prévia do cartão, os vínculos, o aviso de parcial e o texto ao desligar. */
export const portalDaCampanha = (id: string, sinal?: AbortSignal) =>
  pedir<PortalDaCampanha>(`/campanhas/${id}/portal`, comSinal(sinal))

/** Ligar a publicação para um público (201, a leitura nova). */
export const ligarPublicacaoNoPortal = (id: string, publico: PublicoDoPortal) =>
  pedir<PortalDaCampanha>(`/campanhas/${id}/portal/ligar`, { metodo: 'POST', corpo: { publico } })

/** Desligar a publicação para um público (201, a leitura nova): vale em qualquer status. */
export const desligarPublicacaoNoPortal = (id: string, publico: PublicoDoPortal) =>
  pedir<PortalDaCampanha>(`/campanhas/${id}/portal/desligar`, { metodo: 'POST', corpo: { publico } })

// ------------------------------------------------------- metas e previsão (3.8/3.9)

/**
 * O atingimento por eixo (3.8) e a previsão (3.9).
 *
 * As duas telas leem `metaDoPeriodo` e `gap` **da API**, nunca de conta própria: a
 * 3.5 mostra o mesmo gap no KPI, e três implementações da mesma fórmula dariam três
 * números para a pergunta "quanto falta?". A meta pro-rata mora no servidor por
 * isso, e não por economia de código.
 *
 * Os campos aceitam `undefined` explícito (`exactOptionalPropertyTypes`) porque quem
 * chama monta o filtro a partir de estado que pode estar vazio — e "não filtrar" é
 * um caso legítimo, não um erro de tipo.
 */
export interface FiltroDePrevisao {
  readonly periodoDe?: string | undefined
  readonly periodoAte?: string | undefined
  readonly responsavelId?: string | undefined
  readonly pracaId?: string | undefined
  readonly setor?: string | undefined
}

function queryDePrevisao(f: FiltroDePrevisao): string {
  const partes = new URLSearchParams()
  const por = (chave: string, valor: string | undefined): void => {
    if (valor !== undefined && valor !== '') partes.set(chave, valor)
  }
  por('periodo_de', f.periodoDe)
  por('periodo_ate', f.periodoAte)
  por('responsavel_id', f.responsavelId)
  por('praca_id', f.pracaId)
  por('setor', f.setor)
  const texto = partes.toString()
  return texto === '' ? '' : `?${texto}`
}

export const metas = (eixo?: string) =>
  pedir<{ dados: MetaComercial[] }>(`/metas${eixo === undefined || eixo === '' ? '' : `?eixo=${eixo}`}`)

export const criarMeta = (corpo: EntradaDeMeta) =>
  pedir<MetaComercial>('/metas', { metodo: 'POST', corpo })

/** O Desfazer do toast da 3.8 — a meta recém-criada sai inteira. */
export const excluirMeta = (id: string) => pedir<{ ok: true }>(`/metas/${id}`, { metodo: 'DELETE' })

/**
 * O painel da 3.8 — realizado × meta por eixo.
 *
 * Só o PERÍODO viaja: o painel de atingimento não é recortado por executivo nem por
 * praça (o eixo já é o recorte). Mandar os filtros da 3.9 aqui daria uma URL que
 * promete um corte que o servidor não faz.
 */
export const atingimento = (eixo: string, filtro: FiltroDePrevisao = {}) => {
  const query = queryDePrevisao({ periodoDe: filtro.periodoDe, periodoAte: filtro.periodoAte })
  return pedir<Atingimento>(`/metas/atingimento${query === '' ? '?' : `${query}&`}eixo=${eixo}`)
}

export const previsao = (filtro: FiltroDePrevisao = {}) =>
  pedir<Previsao>(`/previsao${queryDePrevisao(filtro)}`)

/** A expansão da linha do executivo (3.8) — só quando alguém clica no chevron. */
export const detalheDoExecutivo = (id: string, filtro: FiltroDePrevisao = {}) =>
  pedir<DetalheDoExecutivo>(
    `/metas/executivo/${id}${queryDePrevisao({ periodoDe: filtro.periodoDe, periodoAte: filtro.periodoAte })}`,
  )

// ------------------------------------------------- atividades comerciais (4.2c)

/**
 * As atividades das fichas 3.2, 3.4 e 3.6.
 *
 * O recorte é obrigatório na API — "todas as atividades da agência" não é pergunta que
 * alguma tela faça —, e por isso a âncora é parâmetro, não opção.
 */
export interface RecorteDeAtividades {
  readonly negocioId?: string | undefined
  readonly empresaId?: string | undefined
  readonly contatoId?: string | undefined
  /** Só a 3.4 usa: lá o período universal governa ESTE bloco (exceção 3, spec 05 §3). */
  readonly periodoDe?: string | undefined
  readonly periodoAte?: string | undefined
}

export const atividades = (recorte: RecorteDeAtividades) => {
  const partes = new URLSearchParams()
  const por = (chave: string, valor: string | undefined): void => {
    if (valor !== undefined && valor !== '') partes.set(chave, valor)
  }
  por('negocio_id', recorte.negocioId)
  por('empresa_id', recorte.empresaId)
  por('contato_id', recorte.contatoId)
  por('periodo_de', recorte.periodoDe)
  por('periodo_ate', recorte.periodoAte)
  return pedir<{ dados: Atividade[] }>(`/atividades?${partes.toString()}`)
}

export const empresasComAtividade = (de: string, ate: string) =>
  pedir<{ total: number }>(`/atividades/empresas-no-periodo?${new URLSearchParams({ periodo_de: de, periodo_ate: ate }).toString()}`)

export const registrarAtividade = (corpo: EntradaDeAtividade) =>
  pedir<Atividade>('/atividades', { metodo: 'POST', corpo })

/** O Desfazer do toast. Soft delete: some da lista, a linha fica no banco. */
export const excluirAtividade = (id: string) =>
  pedir<{ ok: true }>(`/atividades/${id}`, { metodo: 'DELETE' })

/**
 * Concluir a próxima ação do negócio (3.6).
 *
 * UMA requisição, porque é UM caso de uso: zerar a pendência e registrar o que
 * aconteceu acontecem na mesma transação do servidor (DS-163). Duas chamadas daqui
 * deixariam a metade boa gravada quando a segunda falhasse.
 */
export const concluirProximaAcao = (negocioId: string, corpo: EntradaDeConclusao) =>
  pedir<Atividade>(`/negocios/${negocioId}/concluir-proxima-acao`, { metodo: 'POST', corpo })

// ------------------------------------------------------------------- anexos (7.1)

/**
 * Os anexos de um registro.
 *
 * O recorte é OBRIGATÓRIO, do mesmo jeito que o das atividades: "todos os anexos do
 * sistema" não é pergunta que alguma tela faça, e uma rota que devolvesse tudo por
 * omissão viraria paginação infinita no primeiro dia de uso real.
 */
export const anexosDe = (entidadeTipo: EntidadeComAnexo, entidadeId: string) =>
  pedir<{ dados: Anexo[] }>(
    `/anexos?entidade_tipo=${entidadeTipo}&entidade_id=${encodeURIComponent(entidadeId)}`,
  )

/** O upload. `FormData` porque o que sobe é byte — ver `enviarArquivo`. */
export function enviarAnexo(
  entidadeTipo: EntidadeComAnexo,
  entidadeId: string,
  arquivo: File,
): Promise<Anexo> {
  const formulario = new FormData()
  // Os dois campos vão DENTRO do multipart, e antes do arquivo: o servidor confere o
  // dono antes de deixar o byte atravessar, para não gastar a banda de quem envia com
  // 25 MB que serão recusados no fim.
  formulario.append('entidade_tipo', entidadeTipo)
  formulario.append('entidade_id', entidadeId)
  formulario.append('arquivo', arquivo)
  return enviarArquivo<Anexo>('/anexos', formulario)
}

/**
 * A URL de download — pedida NA HORA do clique, nunca guardada.
 *
 * Ela vale cinco minutos (DS-178). Buscá-la junto da lista daria links que expiram
 * enquanto a ficha está aberta, e o segundo clique cairia num "link expirado" sem
 * que nada tivesse mudado na tela.
 */
export const urlDoAnexo = (id: string) => pedir<UrlDeAnexo>(`/anexos/${id}/url`)

/** "Compartilhar no portal" (D-56) — um interruptor, nunca uma cópia do arquivo. */
export const compartilharAnexo = (id: string, compartilhadoPortal: boolean) =>
  pedir<Anexo>(`/anexos/${id}`, { metodo: 'PATCH', corpo: { compartilhadoPortal } })

/**
 * Renomeia o anexo — PÓS-V1-017.
 *
 * Mesmo `PATCH`, mesma permissão (`editar_todos` da entidade dona) e mesma auditoria do
 * compartilhamento. Só o metadado muda: a chave do objeto no Garage continua a mesma, e
 * o `Content-Disposition` do download já lê `nome_arquivo`, então o nome novo acompanha o
 * arquivo baixado. O servidor recusa troca de extensão.
 */
export const renomearAnexo = (id: string, nomeArquivo: string) =>
  pedir<Anexo>(`/anexos/${id}`, { metodo: 'PATCH', corpo: { nomeArquivo } })

/** O Desfazer do toast. Soft delete: some da lista, o byte fica no Garage. */
export const excluirAnexo = (id: string) => pedir<{ ok: true }>(`/anexos/${id}`, { metodo: 'DELETE' })

/** Restaura o mesmo anexo excluído pelo ator dentro da janela curta de Desfazer. */
export const restaurarAnexo = (id: string) =>
  pedir<Anexo>(`/anexos/${id}/restaurar`, { metodo: 'POST' })

// --------------------------------------------------------------- comentários (7.1b)

function recorteDeComentario(entidadeTipo: EntidadeComentavel, entidadeId: string): string {
  const busca = new URLSearchParams({ entidade_tipo: entidadeTipo, entidade_id: entidadeId })
  return busca.toString()
}

export const comentariosDe = (
  entidadeTipo: EntidadeComentavel,
  entidadeId: string,
  sinal?: AbortSignal,
) => pedir<{ dados: Comentario[] }>(
  `/comentarios?${recorteDeComentario(entidadeTipo, entidadeId)}`,
  sinal === undefined ? {} : { sinal },
)

export const publicarComentario = (
  entidadeTipo: EntidadeComentavel,
  entidadeId: string,
  corpo: EntradaDeComentario,
) => pedir<Comentario>(`/comentarios?${recorteDeComentario(entidadeTipo, entidadeId)}`, {
  metodo: 'POST',
  corpo,
})

export const editarComentario = (id: string, corpo: EntradaDeComentario) =>
  pedir<Comentario>(`/comentarios/${id}`, { metodo: 'PATCH', corpo })

export const excluirComentario = (id: string) =>
  pedir<{ ok: true }>(`/comentarios/${id}`, { metodo: 'DELETE' })

export const restaurarComentario = (id: string) =>
  pedir<Comentario>(`/comentarios/${id}/restaurar`, { metodo: 'POST' })

// ------------------------------------------------------------- faturamento (4.4)

/**
 * A fila de faturamento — e ela NÃO recebe período.
 *
 * Exceção 2 da spec 05 §3: o filtro universal governa o LIVRO (por data de emissão) e
 * a fila aparece sempre. Pendência não prescreve — um pedido cuja veiculação acabou em
 * março continua esperando nota em agosto, e escondê-lo por estar fora do recorte seria
 * o sistema ajudando a esquecer.
 */
export const filaDeFaturamento = () =>
  pedir<{
    dados: ItemDaFila[]
    total: number
    soma: string | null
    /** Pedidos vivos SEM agenda: não entram na fila, e não podem sumir da tela. */
    semAgenda: PedidoSemAgenda[]
    /** As parcelas com saldo cuja data de faturar ainda não chegou — a ponte com o selo. */
    aguardando?: AguardandoDataDeFaturar
    /**
     * As mesmas parcelas, LISTADAS: as Programadas para faturar (DS-636). Opcional como o
     * `aguardando`: a API antiga não a manda, e a tela então não mostra a seção.
     */
    programadas?: ItemProgramado[]
  }>('/faturamento/fila')

export function livroDeNfs(recorte: { periodoDe?: string; periodoAte?: string; situacao?: string; q?: string }) {
  const partes = new URLSearchParams()
  const por = (chave: string, valor?: string): void => {
    if (valor !== undefined && valor !== '') partes.set(chave, valor)
  }
  por('periodo_de', recorte.periodoDe)
  por('periodo_ate', recorte.periodoAte)
  por('situacao', recorte.situacao)
  por('q', recorte.q)
  return pedir<{ dados: Nf[]; total: number; faturamentoNoPeriodo: string | null }>(`/nfs?${partes.toString()}`)
}

/**
 * UMA nota, pelo id — o que o deep-link `?nf=` da 4.4 precisa saber antes de tudo.
 *
 * A 4.4 manda o ID (é o que a linha do recebível tem na mão) e a 4.5 precisa do NÚMERO
 * para pré-preencher a busca e da EMISSÃO para saber que período abrir. Procurar no
 * livro já carregado não serve: ele chega recortado pelo período, e a nota do link
 * costuma estar fora dele — foi exatamente isso que o link produziu até aqui, uma tela
 * que abria no mês corrente sem a nota em que a pessoa clicou.
 */
export const nfDoLivro = (id: string) => pedir<Nf>(`/nfs/${id}`)

/** SUGERIDO, não reservado: quem numera é a prefeitura (DS-68). */
export const proximoNumeroDeNf = (serie: string) =>
  pedir<{ serie: string; sugerido: number }>(`/nfs/proximo-numero?serie=${encodeURIComponent(serie)}`)

/**
 * As parcelas do pedido, para o modal de registro da NF.
 *
 * A rota DEDICADA, e não a ficha inteira: o modal precisa de `recebiveis` e nada mais,
 * e puxar `GET /pedidos/:id` traria partes, itens, campanhas e comissões junto — quatro
 * consultas a mais por abertura de modal, e um acoplamento da tela de faturamento com
 * toda mudança na ficha do pedido. É a rota que a spec 4.5 nomeia.
 */
export const recebiveisDoPedido = (pedidoId: string) =>
  pedir<Pedido['recebiveis']>(`/pedidos/${pedidoId}/recebiveis`)

/**
 * A trilha do pedido (4.2) — a auditoria REAL do registro, não uma síntese.
 *
 * Rota própria, e não um campo de `GET /pedidos/:id`: a ficha inteira é recarregada a
 * cada baixa, a cada encerramento e a cada gravação de itens, e arrastar a trilha
 * junto faria uma tabela que só cresce ser varrida a cada ato. Aqui ela recarrega
 * quando faz sentido — quando quem está na tela pede.
 */
export const trilhaDoPedido = (pedidoId: string) =>
  pedir<RespostaDaTrilha>(`/pedidos/${pedidoId}/trilha`)

/** Registrar a NF que a prefeitura emitiu — o número vem no corpo (DS-68). */
export const registrarNf = (corpo: EntradaDeNf) => pedir<Nf>('/nfs', { metodo: 'POST', corpo })

/** O retorno do clearance. No v1 quem o informa é o operador; na fase 12, o gateway. */
export const clearanceDaNf = (id: string, corpo: EntradaDeRetornoDeClearance) =>
  pedir<Nf>(`/nfs/${id}/clearance`, { metodo: 'POST', corpo })

export const cancelarNf = (id: string, corpo: EntradaDeCancelamentoDeNf) =>
  pedir<Nf>(`/nfs/${id}/cancelar`, { metodo: 'POST', corpo })

export const substituirNf = (id: string, corpo: EntradaDeNf) =>
  pedir<Nf>(`/nfs/${id}/substituir`, { metodo: 'POST', corpo })

// ------------------------------------------- autorização para faturar (D-209)

/**
 * O bloco "Aguardando autorização para faturar" da 4.5: todas as parcelas de Pedido sujeito sem autorização vigente,
 * passadas, de hoje ou futuras. Sem período: pendência não prescreve. Quem só autoriza lê esta rota sem ver o resto do
 * Faturamento; o Financeiro a lê com os valores que já vê.
 */
export const aguardandoAutorizacao = () => pedir<RespostaAguardandoAutorizacao>('/faturamento/autorizacoes')

/** A revisão de UMA parcela: o conteúdo que será autorizado, a impressão e a última decisão vista. */
export const revisaoDaAutorizacao = (recebivelId: string) =>
  pedir<RevisaoDaAutorizacao>(`/faturamento/autorizacoes/${recebivelId}`)

export const autorizarFaturamento = (recebivelId: string, corpo: EntradaDeAutorizacao) =>
  pedir<RespostaDaAutorizacao>(`/faturamento/autorizacoes/${recebivelId}`, { metodo: 'POST', corpo })

export const retirarAutorizacao = (recebivelId: string, corpo: EntradaDeRetiradaDaAutorizacao) =>
  pedir<RespostaDaRetiradaDaAutorizacao>(`/faturamento/autorizacoes/${recebivelId}/retirada`, { metodo: 'POST', corpo })

/** As instruções que o Financeiro lê ao registrar a NF (do perfil vigente e complementares do Pedido). */
export const instrucoesDeFaturamento = (pedidoId: string) =>
  pedir<InstrucoesDeFaturamento>(`/faturamento/instrucoes/${pedidoId}`)

/** A prévia do alcance de ligar (ou desligar) a marca do produto no catálogo. */
export const previaDaExigenciaDeAutorizacao = (produtoId: string, ligar: boolean) =>
  pedir<PreviaDaExigenciaDeAutorizacao>(`/catalogos/produtos/${produtoId}/exigencia-de-autorizacao?ligar=${String(ligar)}`)

export const confirmarExigenciaDeAutorizacao = (produtoId: string, corpo: EntradaDaExigenciaDeAutorizacao) =>
  pedir<RespostaDaExigenciaDeAutorizacao>(`/catalogos/produtos/${produtoId}/exigencia-de-autorizacao`, { metodo: 'POST', corpo })

// ------------------------------------------------ recebíveis e cobrança (4.5)

interface FiltrosDeRecebiveis {
  visao?: 'todos' | 'a-vencer' | 'vencidos' | 'recebidos' | 'a-faturar'
  aging?: string
  status?: string
  empresaId?: string
  periodoDe?: string
  periodoAte?: string
  q?: string
  /** Público · Privado — derivado do tipo do Cliente (D-32). */
  setor?: string
  /** `cliente` · `vencimento` · `valor`. O que a API não conhece cai no padrão. */
  ordenar?: string
}

function buscaDeRecebiveis(f: FiltrosDeRecebiveis): URLSearchParams {
  const b = new URLSearchParams()
  if (f.visao !== undefined) b.set('visao', f.visao)
  if (f.aging !== undefined && f.aging !== '') b.set('aging', f.aging)
  if (f.status !== undefined && f.status !== '') b.set('status', f.status)
  if (f.empresaId !== undefined && f.empresaId !== '') b.set('empresa_id', f.empresaId)
  if (f.setor !== undefined && f.setor !== '') b.set('setor', f.setor)
  if (f.ordenar !== undefined && f.ordenar !== '') b.set('ordenar', f.ordenar)
  if (f.periodoDe !== undefined && f.periodoDe !== '') b.set('periodo_de', f.periodoDe)
  if (f.periodoAte !== undefined && f.periodoAte !== '') b.set('periodo_ate', f.periodoAte)
  if (f.q !== undefined && f.q !== '') b.set('q', f.q)
  return b
}

/**
 * Os KPIs da cobrança, prontos do servidor (DS-433).
 *
 * A tela **não** os recalcula: DSO e inadimplência respondem a calendários diferentes
 * (Faturamento e posição), e refazê-los aqui criaria a segunda definição de cada um.
 *
 * `dias` e `inadimplenciaPct` são anuláveis: setor sem nota não tem prazo medido, e nada
 * em aberto não tem proporção de vencido. O cartão diz o que falta — nunca zero.
 */
export interface KpisDaCobranca {
  readonly dso: { setor: string; dias: number | null; base: number; referencia: number | null }[]
  readonly emAberto: string | null
  readonly vencido: string | null
  readonly inadimplenciaPct: string | null
  /** Quantas parcelas compõem o "em aberto" — a legenda do cartão. */
  readonly parcelas: number
  /**
   * O saldo ainda sem nota viva, e em quantas parcelas (DS-636). Fora do Em aberto, do
   * aging e da inadimplência. Opcional: a API antiga não o manda, e a linha não aparece.
   */
  readonly semNf?: { readonly valor: string | null; readonly parcelas: number }
}

export const listarRecebiveis = (f: FiltrosDeRecebiveis) => {
  const qs = buscaDeRecebiveis(f).toString()
  return pedir<{
    data: Recebivel[]
    /** `total` é o universo, sem filtro: é o "de M" do rodapé, e a tela não tem como sabê-lo. */
    meta: { filtros: { clientes: { id: string; nome: string }[] }; kpis: KpisDaCobranca; total: number }
  }>(`/recebiveis${qs === '' ? '' : `?${qs}`}`)
}

export const itensContabeisParaRecebivel = () =>
  pedir<{ data: ItemContabilParaRecebivel[] }>('/recebiveis/itens-contabeis')

export const classificarRecebivel = (id: string, corpo: EntradaDeClassificacaoDoRecebivel) =>
  pedir<{ id: string; itemContabilId: string; itemContabilNome: string }>(`/recebiveis/${id}/item-contabil`, { metodo: 'PATCH', corpo })

/**
 * Reprogramar o faturamento de UMA parcela (0131).
 *
 * Existe separada de `salvarRecebiveis` de propósito: aquela regrava a agenda inteira
 * — apaga e recria — e se recusa a rodar quando alguma parcela já foi faturada. Aqui a
 * intenção é mover uma data, e as irmãs não são tocadas.
 */
export const reprogramarFaturamento = (id: string, corpo: EntradaDeProgramacaoDeFaturamento) =>
  pedir<ProgramacaoDeFaturamento>(`/recebiveis/${id}/programacao-faturamento`, { metodo: 'PATCH', corpo })

/** Exporta o RECORTE da cobrança — os mesmos filtros que a tela está mostrando. */
export const exportarRecebiveis = (f: FiltrosDeRecebiveis) => {
  const busca = buscaDeRecebiveis(f)
  busca.set('formato', 'csv')
  return baixar(`/recebiveis/exportar?${busca.toString()}`)
}

export const darBaixa = (id: string, corpo: EntradaDeLiquidacao) =>
  pedir<BaixaRegistrada>(`/recebiveis/${id}/liquidacoes`, { metodo: 'POST', corpo })

/** As baixas já registradas na parcela — é delas que sai o id que o estorno desfaz. */
export const baixasDoRecebivel = (recebivelId: string) =>
  pedir<{ data: BaixaDoRecebivel[] }>(`/recebiveis/${recebivelId}/baixas`)

export const estornarBaixa = (id: string) =>
  pedir<{ ok: true }>(`/liquidacoes/${id}`, { metodo: 'DELETE' })

export const conferirOrigemDoDesconto = (id: string, corpo: EntradaDeConferenciaDeDesconto) =>
  pedir<{ ok: true }>(`/liquidacoes/${id}/origem`, { metodo: 'PUT', corpo })

// --------------------------------------------------------------- comissões (4.7)

export interface FiltroDeComissoes {
  readonly parceiro?: string
  readonly status?: string
  readonly tipo?: string
  readonly periodoDe?: string
  readonly periodoAte?: string
  readonly q?: string
  /** O recorte da ficha do pedido (4.2) — a lista inteira nunca foi a pergunta dela. */
  readonly pedidoId?: string
}

function queryDeComissoes(f: FiltroDeComissoes): string {
  const b = new URLSearchParams()
  const por = (chave: string, valor: string | undefined): void => {
    if (valor !== undefined && valor !== '') b.set(chave, valor)
  }
  por('parceiro', f.parceiro)
  por('status', f.status)
  por('tipo', f.tipo)
  por('periodo_de', f.periodoDe)
  por('periodo_ate', f.periodoAte)
  por('q', f.q)
  por('pedido', f.pedidoId)
  const texto = b.toString()
  return texto === '' ? '' : `?${texto}`
}

export const comissoes = (f: FiltroDeComissoes = {}) =>
  pedir<{
    data: LinhaDeComissao[]
    meta: {
      agregados: { aPagar: string | null; aguardandoCliente: string | null; pagasNoPeriodo: string | null; comissoesNoPeriodo: string | null }
      contagens: { aPagar: number; aguardandoCliente: number; pagasNoPeriodo: number; comissoesNoPeriodo: number; soRegistro: number }
      filtros: { parceiros: { chave: string; nome: string; natureza: 'PJ' | 'PF'; tipo: 'Padrão' | 'BV/Cashback' }[] }
      capacidades: { podeEditar: boolean; podeExportar: boolean; veValoresFinanceiros: boolean }
    }
  }>(`/comissoes${queryDeComissoes(f)}`)

export const extratoDeComissao = (parceiro: string) =>
  pedir<ExtratoDeComissao>(`/comissoes/extrato?parceiro=${encodeURIComponent(parceiro)}`)

export const gerarPdfDeExtratoDeComissao = (parceiro: string) =>
  pedir<{ id: string; jobId: string; estado: 'enfileirado' }>('/comissoes/extrato/pdf', {
    metodo: 'POST', corpo: { parceiro },
  })

export const solicitarDocumentoDeComissao = (parcelaId: string) =>
  pedir<{ id: string; jobId: string }>(`/parcelas-comissao/${parcelaId}/solicitar-doc`, {
    metodo: 'POST', corpo: {},
  })

export const registrarDocumentoDeComissao = (parcelaId: string, anexoId: string) =>
  pedir<{ id: string }>(`/parcelas-comissao/${parcelaId}/registrar-doc`, {
    metodo: 'POST', corpo: { anexoId },
  })

export const enviarComissaoParaAprovacao = (parcelaId: string) =>
  pedir<{ id: string }>(`/parcelas-comissao/${parcelaId}/enviar-aprovacao`, {
    metodo: 'POST', corpo: {},
  })

export const pagarComissao = (parcelaId: string, corpo: EntradaDePagamentoDeComissao) =>
  pedir<{ id: string; contaPagarIds: string[] }>(`/parcelas-comissao/${parcelaId}/pagar`, {
    metodo: 'POST', corpo,
  })

/** DS-619: a comissão paga validada a menor; a diferença vira crédito contra o parceiro. */
export const validarValorDeComissao = (parcelaId: string, corpo: EntradaDeValidacaoDeComissao) =>
  pedir<{ id: string; valor: string }>(`/parcelas-comissao/${parcelaId}/validar-valor`, {
    metodo: 'POST', corpo,
  })

/** DS-619: sem próximo pagamento, o crédito contra o parceiro se encerra com motivo. */
export const encerrarCreditoDoParceiro = (creditoId: string, motivo: string) =>
  pedir<{ id: string }>(`/creditos-parceiro/${creditoId}/encerrar`, { metodo: 'POST', corpo: { motivo } })

export const configurarGuiaDeRetencao = (parcelaId: string, competencia: string, vencimento: string) =>
  pedir<{ id: string; competenciaGuiaRetencao: string; vencimentoGuiaRetencao: string }>(
    `/parcelas-comissao/${parcelaId}/guia-retencao`,
    { metodo: 'PATCH', corpo: { competencia, vencimento } },
  )

export const estornarPagamentoDeComissao = (liquidacaoId: string) =>
  pedir<{ id: string }>(`/liquidacoes-comissao/${liquidacaoId}`, { metodo: 'DELETE' })

export const exportarComissoes = (f: FiltroDeComissoes = {}) => {
  const busca = new URLSearchParams(queryDeComissoes(f).replace(/^\?/, ''))
  busca.set('formato', 'csv')
  return baixar(`/comissoes/exportar?${busca.toString()}`)
}

export const regrasDeComissao = () => pedir<{ data: RegraDeComissao[] }>('/regras-comissao')

/** Os catálogos da 10.4, sem parceiros (DS-529) e com o setor de cada tipo de cliente (DS-532). */
export const opcoesDeRegrasDeComissao = () =>
  pedir<OpcoesDaRegraDeComissao>('/regras-comissao/opcoes')

/**
 * Os parceiros do seletor da 10.4 (item 03 · DS-529), com permissão de comissões e sem
 * termo de taxa. `capacidades` resolve o valor que a regra grava.
 */
export const parceirosDaRegra = (
  filtro: { readonly q?: string; readonly limite?: number; readonly capacidades?: readonly string[] } = {},
  sinal?: AbortSignal,
) => {
  const busca = new URLSearchParams()
  if (filtro.q !== undefined && filtro.q !== '') busca.set('q', filtro.q)
  if (filtro.limite !== undefined) busca.set('limit', String(filtro.limite))
  if (filtro.capacidades !== undefined && filtro.capacidades.length > 0) busca.set('capacidades', filtro.capacidades.join(','))
  const texto = busca.toString()
  return pedir<RespostaDeParceirosDaRegra>(`/regras-comissao/parceiros${texto === '' ? '' : `?${texto}`}`, comSinal(sinal))
}

export const criarRegraDeComissao = (corpo: EntradaDeRegraDeComissao) =>
  pedir<RegraDeComissao>('/regras-comissao', { metodo: 'POST', corpo })

export const editarRegraDeComissao = (id: string, corpo: EntradaDeRegraDeComissao) =>
  pedir<RegraDeComissao>(`/regras-comissao/${id}`, { metodo: 'PATCH', corpo })

export const excluirRegraDeComissao = (id: string) =>
  pedir<{ excluido: true }>(`/regras-comissao/${id}`, { metodo: 'DELETE' })

// -------------------------------------------------------- ativos intangíveis (Contábil)

export const ativosIntangiveis = () =>
  pedir<RespostaDeAtivosIntangiveis>('/ativos-intangiveis')

export const criarAtivoIntangivel = (corpo: EntradaDeAtivoIntangivel) =>
  pedir<AtivoIntangivel>('/ativos-intangiveis', { metodo: 'POST', corpo })

export const gerarAgendaDoAtivoIntangivel = (id: string) =>
  pedir<AtivoIntangivel>(`/ativos-intangiveis/${id}/agenda`, { metodo: 'POST', corpo: {} })

export const reordenarRegrasDeComissao = (ids: readonly string[]) =>
  pedir<{ id: string; data: RegraDeComissao[] }>('/regras-comissao/reordenar', { metodo: 'POST', corpo: { ids } })

export interface ExemploDeRegraDeComissao {
  readonly parceiroCapacidadeId?: string | null
  readonly tipoParceiroId?: string | null
  readonly tipoEmpresaClienteId?: string | null
  readonly setor?: 'Público' | 'Privado' | null
  readonly produtoId?: string | null
  readonly data: string
}

export const simularRegraDeComissao = (corpo: ExemploDeRegraDeComissao) =>
  pedir<{ regra: RegraDeComissao | null; demais: RegraDeComissao[] }>('/regras-comissao/simular', { metodo: 'POST', corpo })

export const historicoDeCobranca = (id: string) =>
  pedir<{ data: ContatoDeCobranca[] }>(`/recebiveis/${id}/cobranca`)

export const registrarContatoDeCobranca = (id: string, corpo: unknown) =>
  pedir<{ id: string }>(`/recebiveis/${id}/contato-cobranca`, { metodo: 'POST', corpo })

export const reverterContatoDeCobranca = (id: string) =>
  pedir<{ ok: true; id: string; recebivelId: string }>(`/contatos-cobranca/${id}/reverter`, { metodo: 'POST', corpo: {} })

export const suspenderRegua = (id: string, motivo: string) =>
  pedir<{ ok: true }>(`/recebiveis/${id}/suspender-regua`, { metodo: 'POST', corpo: { motivo } })

export const reativarRegua = (id: string) =>
  pedir<{ ok: true }>(`/recebiveis/${id}/reativar-regua`, { metodo: 'POST', corpo: {} })

export const registrarPerdaEstimada = (id: string, corpo: { percentual: string; motivo: string }) =>
  pedir<{ ok: true }>(`/recebiveis/${id}/perda-estimada`, { metodo: 'POST', corpo })

/**
 * As contas de UMA entidade nomeada — a 10.2 mostra as contas da entidade que ela abriu,
 * e não as da entidade do seletor do cabeçalho.
 *
 * Rota separada, e não um parâmetro na lista comum: a permissão dela é a de administrar
 * entidades, e não a de tesouraria. Ver o cabeçalho do endpoint.
 */
export const contasDaEntidade = (entidadeId: string) =>
  pedir<{ data: ContaBancaria[] }>(`/contas-bancarias/da-entidade/${entidadeId}`)

export const contasBancarias = () => pedir<{ data: ContaBancaria[] }>('/contas-bancarias')

export const reguaDeCobranca = () => pedir<{ data: PassoDaRegua[] }>('/cobranca/regua')

export const salvarReguaDeCobranca = (passos: PassoDaRegua[]) =>
  pedir<{ data: PassoDaRegua[] }>('/cobranca/regua', { metodo: 'PUT', corpo: { passos } })

// -------------------------------------------------------- tesouraria (4.6)

export const posicaoDeCaixa = () =>
  pedir<{
    data: SaldoDaConta[]
    meta: { total: string; disponivel: string; bloqueado: string; contas: number }
  }>('/caixa/posicao')

/** Enfileira a releitura da conta; nenhuma chamada bancária ocorre dentro da requisição. */
export const sincronizarSicoob = (contaBancariaId: string) =>
  pedir<{
    id: string
    jobId: string
    contaBancariaId: string
    estado: 'enfileirada'
  }>(
    `/contas-bancarias/${contaBancariaId}/sincronizar`, {
    metodo: 'POST',
    corpo: {},
    },
  )

/** O estado REAL da integração bancária — conectada, último sync, validade do certificado. */
export const integracaoBancaria = () => pedir<EstadoDaIntegracao>('/caixa/integracao')

/** As divergências bancárias do Sicoob: as vivas e as resolvidas nos últimos 30 dias (D-162). */
export const divergenciasSicoob = () => pedir<{ data: DivergenciaSicoob[] }>('/caixa/sicoob/divergencias')

/** A decisão de uma pessoa sobre uma divergência: aceitar, manter ou reconhecer a ausência. */
export const resolverDivergenciaSicoob = (id: string, entrada: EntradaDeResolucaoDeDivergencia) =>
  pedir<DivergenciaSicoob>(`/caixa/sicoob/divergencias/${id}/resolver`, { metodo: 'POST', corpo: entrada })

/** DS-621: a divergência do extrato de investimento — a linha que o banco mudou e espera decisão humana. */
export const divergenciasDeInvestimento = () =>
  pedir<{ data: DivergenciaDeInvestimento[] }>('/caixa/sicoob/investimentos/divergencias')

/** DS-621: aceitar a versão do banco ou manter a registrada, com motivo. A decisão só se acrescenta. */
export const decidirDivergenciaDeInvestimento = (linhaId: string, entrada: EntradaDeDecisaoDeInvestimento) =>
  pedir<{ linhaId: string }>(`/caixa/sicoob/investimentos/divergencias/${linhaId}/decidir`, { metodo: 'POST', corpo: entrada })

/** DS-621 (D-175): o IRRF retido em aplicações no período, no razão e sem classificação contábil. */
export const irrfRetidoEmAplicacoes = (de: string, ate: string) =>
  pedir<IrrfRetidoEmAplicacoes>(`/tributos/irrf-aplicacoes?${new URLSearchParams({ de, ate }).toString()}`)

/** Upload explicitamente iniciado pela pessoa; a conta vem antes do arquivo no multipart. */
export function importarExtrato(
  contaBancariaId: string,
  arquivo: File,
): Promise<ResultadoDaImportacaoDeMovimentos> {
  const formulario = new FormData()
  formulario.append('conta_bancaria_id', contaBancariaId)
  formulario.append('arquivo', arquivo)
  return enviarArquivo<ResultadoDaImportacaoDeMovimentos>('/movimentos/importar', formulario)
}

/** A curva de saldo REALIZADA (G20) e as saídas médias mensais do rodapé do cartão da 6.2. */
export const curvaDeCaixa = () =>
  pedir<{ data: PontoDaCurva[]; meta: { runway: RunwayDeCaixa } }>('/caixa/curva')

export const agendaDeCaixa = (f: { de: string; ate: string }) => {
  const busca = new URLSearchParams({ de: f.de, ate: f.ate })
  return pedir<{
    data: LinhaDaAgendaDeCaixa[]
    meta: {
      agregados: AgregadosDaAgendaDeCaixa
      escopoParcial: string[]
      podeExportar: boolean
      /** A versão aprovada do período tem receita e nenhum Recebimento (PK-11). */
      orcadoSemRecebimento: boolean
      /** DS-615: o tributo sem regra no calendário fiscal, fora da curva datada. */
      aguardandoConfiguracaoFiscal: PendenciaFiscalDoCaixa[]
      /** DS-620: a previsão vencida com restante (a entrada fora da curva; a saída em hoje). */
      previsoesVencidas?: PrevisaoVencidaDoCaixa[]
    }
  }>(`/caixa/agenda?${busca.toString()}`)
}

// --------------------------------------------------------------- previsão de caixa (DS-620)

export const previsoesDeCaixa = () => pedir<{ data: PrevisaoDeCaixa[] }>('/previsoes-caixa')

export const criarPrevisaoDeCaixa = (corpo: EntradaDePrevisaoDeCaixa) =>
  pedir<{ id: string }>('/previsoes-caixa', { metodo: 'POST', corpo })

export const alterarPrevisaoDeCaixa = (id: string, corpo: EntradaDeAlteracaoDaPrevisao) =>
  pedir<{ id: string }>(`/previsoes-caixa/${id}`, { metodo: 'PATCH', corpo })

export const finalizarPrevisaoDeCaixa = (id: string, acao: 'encerrar' | 'cancelar', motivo: string) =>
  pedir<{ id: string }>(`/previsoes-caixa/${id}/${acao}`, { metodo: 'POST', corpo: { motivo } })

// --------------------------------------------------------------- tributos (DS-615)

export const apuracoesTributarias = (ano?: number) =>
  pedir<RespostaDasApuracoes>(`/tributos/apuracoes${ano === undefined ? '' : `?ano=${String(ano)}`}`)

export const detalheDaApuracaoTributaria = (tributo: TributoFiscalDoContrato, periodo: string) =>
  pedir<DetalheDaApuracao>(`/tributos/apuracoes/detalhe?${new URLSearchParams({ tributo, periodo }).toString()}`)

export const fecharApuracaoTributaria = (corpo: EntradaDeFechamentoDaApuracao) =>
  pedir<{ id: string; contaPagarId: string | null }>('/tributos/apuracoes', { metodo: 'POST', corpo })

export const reabrirApuracaoTributaria = (id: string, motivo: string) =>
  pedir<{ id: string }>(`/tributos/apuracoes/${id}/reabrir`, { metodo: 'POST', corpo: { motivo } })

export const informarProvisaoTributaria = (corpo: EntradaDeProvisao) =>
  pedir<{ id: string }>('/tributos/provisoes', { metodo: 'POST', corpo })

export const cancelarProvisaoTributaria = (id: string, motivo: string) =>
  pedir<{ id: string }>(`/tributos/provisoes/${id}/cancelar`, { metodo: 'POST', corpo: { motivo } })

export const calendarioFiscal = () => pedir<{ data: RegraDoCalendarioFiscal[] }>('/calendario-fiscal')

export const opcoesDoCalendarioFiscal = () => pedir<OpcoesDoCalendarioFiscal>('/calendario-fiscal/opcoes')

export const criarRegraFiscal = (corpo: EntradaDeRegraFiscal) =>
  pedir<RegraDoCalendarioFiscal>('/calendario-fiscal', { metodo: 'POST', corpo })

export const encerrarRegraFiscal = (id: string, vigenciaAte: string) =>
  pedir<RegraDoCalendarioFiscal>(`/calendario-fiscal/${id}`, { metodo: 'PATCH', corpo: { vigenciaAte } })

export const exportarAgendaDeCaixa = (f: {
  de: string
  ate: string
  conta?: string
  camada?: CamadaDeCaixa
}) => {
  const busca = new URLSearchParams({ formato: 'csv', de: f.de, ate: f.ate })
  if (f.conta !== undefined && f.conta !== '') busca.set('conta', f.conta)
  if (f.camada !== undefined) busca.set('camada', f.camada)
  return baixar(`/caixa/agenda/exportar?${busca.toString()}`)
}

export const caixaExecutado = (f: { de: string; ate: string }) => {
  const busca = new URLSearchParams({ de: f.de, ate: f.ate })
  return pedir<{ data: LinhaDoCaixaExecutado[] }>(`/caixa/executado?${busca.toString()}`)
}

/** A curva projetada diária e o horizonte projetado de caixa (DS-580), na mesma rota com período. */
export const curvaProjetadaDeCaixa = (f: { de: string; ate: string; horizonte?: number }) => {
  const busca = new URLSearchParams({ de: f.de, ate: f.ate })
  if (f.horizonte !== undefined) busca.set('horizonte', String(f.horizonte))
  return pedir<{ data: PontoDaCurvaProjetada[]; meta: ResumoDaCurvaProjetada }>(
    `/caixa/curva?${busca.toString()}`,
  )
}

export const extratoDeCaixa = (f: { contaId?: string; periodoDe?: string; periodoAte?: string }) => {
  const b = new URLSearchParams()
  if (f.contaId !== undefined && f.contaId !== '') b.set('conta_id', f.contaId)
  if (f.periodoDe !== undefined && f.periodoDe !== '') b.set('periodo_de', f.periodoDe)
  if (f.periodoAte !== undefined && f.periodoAte !== '') b.set('periodo_ate', f.periodoAte)
  const qs = b.toString()
  const base = f.contaId === undefined || f.contaId === ''
    ? '/caixa/extrato'
    : `/contas-bancarias/${f.contaId}/extrato`
  return pedir<{ data: MovimentoBancario[] }>(`${base}${qs === '' ? '' : `?${qs}`}`)
}

/** PIX confirmado pelo endpoint próprio, ainda sem a linha correspondente no extrato mensal. */
export const pixPendentesDeCaixa = (f: { contaId?: string; periodoDe?: string; periodoAte?: string }) => {
  const b = new URLSearchParams()
  if (f.contaId !== undefined && f.contaId !== '') b.set('conta_id', f.contaId)
  if (f.periodoDe !== undefined && f.periodoDe !== '') b.set('periodo_de', f.periodoDe)
  if (f.periodoAte !== undefined && f.periodoAte !== '') b.set('periodo_ate', f.periodoAte)
  const qs = b.toString()
  return pedir<{ data: PixRecebidoPendente[] }>(`/caixa/pix-pendentes${qs === '' ? '' : `?${qs}`}`)
}

export const lancarMovimento = (corpo: EntradaDeMovimento) =>
  pedir<{ id: string }>('/caixa/movimentos', { metodo: 'POST', corpo })

export const transferirEntreContas = (corpo: EntradaDeTransferencia) =>
  pedir<TransferenciaRegistrada>('/transferencias', { metodo: 'POST', corpo })

export const estornarTransferencia = (id: string) =>
  pedir<{ id: string; estornada: true }>(`/transferencias/${id}/estornar`, { metodo: 'POST', corpo: {} })

// ----------------------------------------------------- contas a pagar (6.4)

export interface FiltrosDeContasPagar {
  readonly alcada?: string
  readonly status?: string
  readonly fornecedorId?: string
  readonly periodoDe?: string
  readonly periodoAte?: string
  readonly q?: string
  readonly ordenacao?: string
  readonly pagina?: number
}

export const contasPagar = (f: FiltrosDeContasPagar = {}) => {
  const busca = new URLSearchParams()
  if (f.alcada !== undefined && f.alcada !== '') busca.set('alcada', f.alcada)
  if (f.status !== undefined && f.status !== '') busca.set('status', f.status)
  if (f.fornecedorId !== undefined && f.fornecedorId !== '') busca.set('forn', f.fornecedorId)
  if (f.periodoDe !== undefined && f.periodoDe !== '') busca.set('periodo_de', f.periodoDe)
  if (f.periodoAte !== undefined && f.periodoAte !== '') busca.set('periodo_ate', f.periodoAte)
  if (f.q !== undefined && f.q !== '') busca.set('q', f.q)
  if (f.ordenacao !== undefined && f.ordenacao !== '') busca.set('sort', f.ordenacao)
  if (f.pagina !== undefined) busca.set('page', String(f.pagina))
  const query = busca.toString()
  return pedir<{
    data: LinhaDeContaPagar[]
    meta: {
      page: number
      limit: number
      total: number
      totalLiquido: string
      totalLiquidoPagina: string
      alcadas: string[]
      agregados: AgregadosDeContasPagar
      moedaBase: 'BRL'
    }
  }>(`/contas-pagar${query === '' ? '' : `?${query}`}`)
}

export const opcoesDeContasPagar = () => pedir<OpcoesDeContaPagar>('/contas-pagar/opcoes')
export const contaPagar = (id: string) => pedir<ContaPagar>(`/contas-pagar/${id}`)
export const criarContaPagar = (corpo: EntradaDeContaPagar) =>
  pedir<{ id: string; codigo: string; jobId: string | null }>('/contas-pagar', { metodo: 'POST', corpo })
export const atualizarRateioDaContaPagar = (id: string, linhas: readonly EntradaDeRateioDaContaPagar[]) =>
  pedir<{ id: string }>(`/contas-pagar/${id}/rateio`, { metodo: 'PUT', corpo: { linhas } })
export const excluirContaPagarRecemCriada = (id: string) =>
  pedir<{ ok: true }>(`/contas-pagar/${id}`, { metodo: 'DELETE' })
export const restaurarContaPagarRecemCriada = (id: string) =>
  pedir<{ id: string }>(`/contas-pagar/${id}/restaurar`, { metodo: 'POST', corpo: {} })
export const registrarDocumentoDaContaPagar = (parcelaId: string, corpo: EntradaDeRegistroDeDocumento) =>
  pedir<{ id: string }>(`/parcelas-pagar/${parcelaId}/registrar-nf`, { metodo: 'POST', corpo })
export const reverterDocumentoDaContaPagar = (parcelaId: string) =>
  pedir<{ id: string }>(`/parcelas-pagar/${parcelaId}/reverter-registro-nf`, { metodo: 'POST', corpo: {} })
export const enviarContaPagarParaAprovacao = (parcelaId: string) =>
  pedir<{ id: string }>(`/parcelas-pagar/${parcelaId}/enviar-aprovacao`, { metodo: 'POST', corpo: {} })
export const registrarPagamentoDaContaPagar = (parcelaId: string, corpo: EntradaDePagamentoDaContaPagar) =>
  pedir<{ id: string }>(`/parcelas-pagar/${parcelaId}/registrar-pagamento`, { metodo: 'POST', corpo })
export const estornarPagamentoDaContaPagar = (liquidacaoId: string) =>
  pedir<{ ok: true }>(`/liquidacoes-pagar/${liquidacaoId}`, { metodo: 'DELETE' })
export const cancelarContaPagar = (id: string, corpo: EntradaDeCancelamentoDaContaPagar) =>
  pedir<{ id: string }>(`/contas-pagar/${id}/cancelar`, { metodo: 'POST', corpo })
export const reverterCancelamentoDaContaPagar = (id: string) =>
  pedir<{ id: string }>(`/contas-pagar/${id}/reverter`, { metodo: 'POST', corpo: {} })

// ---------------------------------------------------------- aprovações (6.5)

export const aprovacoesPendentes = () => pedir<RespostaDaFilaDeAprovacoes>('/aprovacoes/pendentes')

export const historicoDeAprovacoes = (f: { de?: string; ate?: string }) => {
  const busca = new URLSearchParams()
  if (f.de !== undefined && f.de !== '') busca.set('de', f.de)
  if (f.ate !== undefined && f.ate !== '') busca.set('ate', f.ate)
  const query = busca.toString()
  return pedir<RespostaDoHistoricoDeAprovacoes>(`/aprovacoes/historico${query === '' ? '' : `?${query}`}`)
}

export const resolverLinkDeAprovacao = (token: string) =>
  pedir<LinkDeAprovacaoResolvido>(`/aprovacoes/link/${encodeURIComponent(token)}`)

function caminhoDaDecisao(tipo: TipoDeAlvoDeAprovacao, id: string, acao: 'aprovar' | 'rejeitar'): string {
  const plural = tipo === 'parcela_pagar' ? 'parcelas-pagar'
    : tipo === 'parcela_comissao' ? 'parcelas-comissao' : 'pagamentos-rh'
  return `/${plural}/${id}/${acao}`
}

export const aprovarDesembolso = (tipo: TipoDeAlvoDeAprovacao, id: string) =>
  pedir<ResultadoDaDecisaoDeDesembolso>(caminhoDaDecisao(tipo, id, 'aprovar'), { metodo: 'POST', corpo: {} })

export const rejeitarDesembolso = (tipo: TipoDeAlvoDeAprovacao, id: string, corpo: EntradaDeRejeicaoDeDesembolso) =>
  pedir<ResultadoDaDecisaoDeDesembolso>(caminhoDaDecisao(tipo, id, 'rejeitar'), { metodo: 'POST', corpo })

// -------------------------------------------------------- conciliação (6.2)

export interface MetaDaBandejaDeConciliacao {
  readonly contaId: string | null
  readonly agregados: {
    readonly aguardando: number
    readonly scoreAlto: number
    readonly semSugestao: number
    readonly confirmadosHoje: number
    readonly saldoBancario: string
    readonly saldoReconciliado: string
    readonly diferenca: string
  }
}

export const bandejaDeConciliacao = (contaId?: string) =>
  pedir<{ data: ItemDaBandejaDeConciliacao[]; meta: MetaDaBandejaDeConciliacao }>(
    `/conciliacao/bandeja${contaId === undefined || contaId === '' ? '' : `?conta=${encodeURIComponent(contaId)}`}`,
  )

export const candidatosDaConciliacao = (f: {
  movimentoId?: string
  q?: string
  direcao?: 'credito' | 'debito'
  valorMin?: string
  valorMax?: string
  de?: string
  ate?: string
}) => {
  const b = new URLSearchParams()
  if (f.movimentoId !== undefined) b.set('movimento', f.movimentoId)
  if (f.q !== undefined && f.q !== '') b.set('q', f.q)
  if (f.direcao !== undefined) b.set('direcao', f.direcao)
  if (f.valorMin !== undefined && f.valorMin !== '') b.set('valor_min', f.valorMin)
  if (f.valorMax !== undefined && f.valorMax !== '') b.set('valor_max', f.valorMax)
  if (f.de !== undefined && f.de !== '') b.set('de', f.de)
  if (f.ate !== undefined && f.ate !== '') b.set('ate', f.ate)
  const qs = b.toString()
  return pedir<{ data: CandidatoDaConciliacao[] }>(`/conciliacao/candidatos${qs === '' ? '' : `?${qs}`}`)
}

export const confirmarConciliacao = (movimentoId: string, corpo: EntradaDeConciliacao) =>
  pedir<{ id: string }>(`/movimentos/${movimentoId}/conciliar`, { metodo: 'POST', corpo })

/** DS-614: a conta a pagar parecida com o débito, antes de lançá-lo como avulso. Avisa; não impede. */
export const capsSemelhantesAoMovimento = (movimentoId: string) =>
  pedir<{ data: ContaAPagarSemelhante[] }>(`/movimentos/${movimentoId}/caps-semelhantes`)

export const ignorarMovimento = (movimentoId: string, corpo: { motivoId: string; observacao?: string }) =>
  pedir<{ id: string }>(`/movimentos/${movimentoId}/ignorar`, { metodo: 'POST', corpo })

export const marcarSemCorrespondencia = (movimentoId: string) =>
  pedir<{ id: string }>(`/movimentos/${movimentoId}/sem-correspondencia`, { metodo: 'POST', corpo: {} })

export const reativarMovimento = (movimentoId: string) =>
  pedir<{ id: string }>(`/movimentos/${movimentoId}/reativar`, { metodo: 'POST', corpo: {} })

export const desfazerConciliacao = (conciliacaoId: string) =>
  pedir<{ id: string }>(`/conciliacoes/${conciliacaoId}/desfazer`, { metodo: 'POST', corpo: {} })

export const regrasDaConciliacao = () => pedir<{ data: RegraDaConciliacao[] }>('/regras-conciliacao')

export const criarRegraDaConciliacao = (corpo: EntradaDeRegraDaConciliacao) =>
  pedir<{ id: string }>('/regras-conciliacao', { metodo: 'POST', corpo })

export const editarRegraDaConciliacao = (id: string, corpo: EntradaDeRegraDaConciliacao) =>
  pedir<{ id: string }>(`/regras-conciliacao/${id}`, { metodo: 'PATCH', corpo })

export const excluirRegraDaConciliacao = (id: string) =>
  pedir<{ id: string }>(`/regras-conciliacao/${id}`, { metodo: 'DELETE' })

// ------------------------------------------------------------- núcleo (4.8)

export const carregarCockpit = (f: { periodoDe: string; periodoAte: string }, sinal?: AbortSignal) => {
  const b = new URLSearchParams({ periodo_de: f.periodoDe, periodo_ate: f.periodoAte })
  return pedir<Cockpit>(`/dashboard/cockpit?${b.toString()}`, sinal === undefined ? {} : { sinal })
}

export const buscarGlobalmente = (q: string, escopo = '', sinal?: AbortSignal) => {
  const b = new URLSearchParams({ q })
  if (escopo !== '') b.set('escopo', escopo)
  return pedir<RespostaDaBuscaGlobal>(`/busca?${b.toString()}`, sinal === undefined ? {} : { sinal })
}

// ------------------------------------------------------ relatórios · 8.7

import type {
  IdDeRelatorio,
  RespostaDeJobsMortos,
  RespostaDoCatalogo,
  RespostaDoLogDeAlteracoes,
} from '@dv/contracts'

/**
 * O catálogo já vem filtrado por permissão (DS-28) — a tela não conhece a matriz.
 *
 * É por isso que a 8.7 não precisa perguntar "posso ver auditoria?": a resposta a
 * essa pergunta é a PRESENÇA do card `log` na lista. Um deep-link `?relatorio=` que
 * não casa com nenhum card visível é, pela mesma razão, "não encontrado" —
 * indistinguível de inexistente, como a DS-28 manda.
 */
export const catalogoDeRelatorios = (sinal?: AbortSignal) =>
  pedir<RespostaDoCatalogo>('/relatorios', sinal === undefined ? {} : { sinal })

export interface FiltrosDoLogDeAlteracoes {
  readonly entidadeTipo?: string
  readonly entidadeId?: string
  readonly ator?: string
  readonly acao?: string
  readonly de?: string
  readonly ate?: string
  readonly page?: number
}

function buscaDoLog(f: FiltrosDoLogDeAlteracoes): URLSearchParams {
  const b = new URLSearchParams()
  if (f.entidadeTipo !== undefined && f.entidadeTipo !== '') b.set('entidade_tipo', f.entidadeTipo)
  if (f.entidadeId !== undefined && f.entidadeId !== '') b.set('entidade_id', f.entidadeId)
  if (f.ator !== undefined && f.ator !== '') b.set('ator', f.ator)
  if (f.acao !== undefined && f.acao !== '') b.set('acao', f.acao)
  if (f.de !== undefined && f.de !== '') b.set('de', f.de)
  if (f.ate !== undefined && f.ate !== '') b.set('ate', f.ate)
  if (f.page !== undefined) b.set('page', String(f.page))
  return b
}

export const logDeAlteracoes = (f: FiltrosDoLogDeAlteracoes = {}, sinal?: AbortSignal) =>
  pedir<RespostaDoLogDeAlteracoes>(
    `/auditoria?${buscaDoLog(f).toString()}`,
    sinal === undefined ? {} : { sinal },
  )

/** DS-38 — o download leva os MESMOS filtros da lista, e o servidor loga o ato. */
export const exportarLogDeAlteracoes = (f: FiltrosDoLogDeAlteracoes = {}) => {
  const b = buscaDoLog(f)
  b.delete('page')
  b.set('formato', 'csv')
  return baixar(`/auditoria/exportar?${b.toString()}`)
}

export const jobsMortos = (f: { de?: string; ate?: string; page?: number } = {}, sinal?: AbortSignal) => {
  const b = new URLSearchParams()
  if (f.de !== undefined && f.de !== '') b.set('de', f.de)
  if (f.ate !== undefined && f.ate !== '') b.set('ate', f.ate)
  if (f.page !== undefined) b.set('page', String(f.page))
  return pedir<RespostaDeJobsMortos>(
    `/relatorios/jobs?${b.toString()}`,
    sinal === undefined ? {} : { sinal },
  )
}

/** Preferência do usuário, autorizada pelo próprio módulo Relatórios (D-106). */
export const salvarFavoritosDeRelatorios = (favoritos: readonly IdDeRelatorio[]) =>
  pedir<{ favoritos: IdDeRelatorio[] }>('/relatorios/favoritos', {
    metodo: 'PATCH',
    corpo: { favoritosRelatorios: favoritos },
  })

// ------------------------------------------------------- plano de contas · 8.1

export const planoDeContas = () => pedir<RespostaDoPlanoDeContas>('/contas-contabeis')
export const criarContaContabil = (corpo: EntradaDeContaContabil) =>
  pedir<ContaContabil>('/contas-contabeis', { metodo: 'POST', corpo })
export const editarContaContabil = (id: string, corpo: EdicaoDeContaContabil) =>
  pedir<RespostaDeEdicaoDeContaContabil>(`/contas-contabeis/${id}`, { metodo: 'PATCH', corpo })
export const excluirContaContabil = (id: string) =>
  pedir<{ desativada: true }>(`/contas-contabeis/${id}`, { metodo: 'DELETE' })

// ------------------------------------------------------- orçamento · 8.1/8.2

export const orcamentos = (ano?: number, sinal?: AbortSignal) => {
  const busca = ano === undefined ? '' : `?ano=${String(ano)}`
  return pedir<RespostaDosOrcamentos>(`/orcamentos${busca}`, sinal === undefined ? {} : { sinal })
}

export const linhasDoOrcamento = (versaoId: string, sinal?: AbortSignal) =>
  pedir<RespostaDasLinhasDeOrcamento>(
    `/versoes/${versaoId}/linhas`,
    sinal === undefined ? {} : { sinal },
  )

export const apoioDoOrcamento = (versaoId: string, sinal?: AbortSignal) =>
  pedir<ApoioAsLinhasDoOrcamento>(`/versoes/${versaoId}/apoio`, sinal === undefined ? {} : { sinal })

export const criarOrcamento = (corpo: EntradaDeNovoOrcamento) =>
  pedir<{ id: string; entityId: string; ano: number; moeda: string }>('/orcamentos', { metodo: 'POST', corpo })

export const criarVersaoDeOrcamento = (orcamentoId: string, corpo: EntradaDeNovaVersaoDeOrcamento) =>
  pedir<{ id: string; entityId: string }>(`/orcamentos/${orcamentoId}/versoes`, { metodo: 'POST', corpo })

export const excluirVersaoDeOrcamento = (versaoId: string) =>
  pedir<{ id: string; excluida: true }>(`/versoes/${versaoId}`, { metodo: 'DELETE' })

export const salvarLinhasDoOrcamento = (versaoId: string, corpo: EntradaDeLinhasDeOrcamento) =>
  pedir<RespostaDasLinhasDeOrcamento>(`/versoes/${versaoId}/linhas`, { metodo: 'PUT', corpo })

/** D-178, DS-623: a prévia de um ato do orçamento (converter, voltar a Manual, atualizar cálculos, classificação). */
export const previaDoAtoDoOrcamento = (versaoId: string, corpo: EntradaDaPreviaDeAto) =>
  pedir<PrevisaoDoAto>(`/versoes/${versaoId}/atos/previa`, { metodo: 'POST', corpo })

/** Aplica o ato com a impressão digital da prévia vista; 409 se a versão mudou desde então. */
export const aplicarAtoDoOrcamento = (versaoId: string, corpo: EntradaDoAtoDeOrcamento) =>
  pedir<RespostaDasLinhasDeOrcamento>(`/versoes/${versaoId}/atos`, { metodo: 'POST', corpo })

export type TransicaoDeOrcamento =
  | 'submeter' | 'aprovar' | 'rejeitar' | 'bloquear' | 'desbloquear' | 'fechar' | 'reabrir'

export const transicionarVersaoDeOrcamento = (versaoId: string, transicao: TransicaoDeOrcamento, motivo?: string) =>
  pedir<{ id: string; estado: string }>(`/versoes/${versaoId}/${transicao}`, {
    metodo: 'POST',
    ...(motivo === undefined ? {} : { corpo: { motivo } }),
  })

export const fecharPeriodoDoOrcamento = (versaoId: string, mes: number) =>
  pedir<{ id: string; mes: number; fechado: true }>(`/versoes/${versaoId}/fechar-periodo`, {
    metodo: 'POST', corpo: { mes },
  })

export const reabrirPeriodoDoOrcamento = (versaoId: string, mes: number, motivo: string) =>
  pedir<{ id: string; mes: number; reaberto: true }>(`/versoes/${versaoId}/reabrir-periodo`, {
    metodo: 'POST', corpo: { mes, motivo },
  })

// --------------------------------------------------------------- DRE · 8.3

export const dre = (
  filtros: {
    readonly periodoDe: string
    readonly periodoAte: string
    readonly itemContabilId?: string | undefined
    readonly setor?: 'Público' | 'Privado' | undefined
  },
  sinal?: AbortSignal,
) => {
  const busca = new URLSearchParams({
    periodo_de: filtros.periodoDe,
    periodo_ate: filtros.periodoAte,
    serie: 'mensal',
  })
  if (filtros.itemContabilId !== undefined) busca.set('item_contabil_id', filtros.itemContabilId)
  if (filtros.setor !== undefined) busca.set('setor', filtros.setor)
  return pedir<RespostaDaDre>(`/dre?${busca.toString()}`, sinal === undefined ? {} : { sinal })
}

export const exportarDre = (filtros: {
  readonly periodoDe:string
  readonly periodoAte:string
  readonly itemContabilId?:string | undefined
  readonly setor?:'Público'|'Privado' | undefined
}) => {
  const busca=new URLSearchParams({periodo_de:filtros.periodoDe,periodo_ate:filtros.periodoAte,serie:'mensal',formato:'pdf'})
  if (filtros.itemContabilId!==undefined) busca.set('item_contabil_id',filtros.itemContabilId)
  if (filtros.setor!==undefined) busca.set('setor',filtros.setor)
  return pedir<{jobId:string;estado:'enfileirado'}>(`/dre/exportar?${busca.toString()}`)
}

// --------------------------------------------- Variância orç × real · 8.4 (tela 8.3)

export interface FiltrosDaVarianciaNaTela {
  readonly periodoDe: string
  readonly periodoAte: string
  readonly versaoId?: string | undefined
  readonly centroCustoId?: string | undefined
}

function buscaDaVariancia(f: FiltrosDaVarianciaNaTela): URLSearchParams {
  const busca = new URLSearchParams({ periodo_de: f.periodoDe, periodo_ate: f.periodoAte })
  if (f.versaoId !== undefined && f.versaoId !== '') busca.set('v', f.versaoId)
  if (f.centroCustoId !== undefined && f.centroCustoId !== '') busca.set('cc', f.centroCustoId)
  return busca
}

export const variancia = (f: FiltrosDaVarianciaNaTela, sinal?: AbortSignal) =>
  pedir<RespostaDaVariancia>(
    `/variancia?${buscaDaVariancia(f).toString()}`,
    sinal === undefined ? {} : { sinal },
  )

/** O drill: os lançamentos que compõem o realizado da linha — a mesma fonte da DRE. */
export const lancamentosDaVariancia = (
  f: FiltrosDaVarianciaNaTela & { readonly contaContabilId: string; readonly semCentroCusto?: boolean },
  sinal?: AbortSignal,
) => {
  const busca = buscaDaVariancia(f)
  busca.set('conta', f.contaContabilId)
  if (f.semCentroCusto === true) busca.set('sem_cc', '1')
  return pedir<RespostaDosLancamentos>(
    `/variancia/lancamentos?${busca.toString()}`,
    sinal === undefined ? {} : { sinal },
  )
}

/** DS-38 — leva os MESMOS filtros da tela, e o servidor registra o ato. */
export const exportarVariancia = (f: FiltrosDaVarianciaNaTela) => {
  const busca = buscaDaVariancia(f)
  busca.set('formato', 'csv')
  return baixar(`/variancia/exportar?${busca.toString()}`)
}

// --------------------------------------- Rentabilidade por Cliente · 8.5 (tela 8.6)

export interface FiltrosDaRentabilidadeNaTela {
  readonly periodoDe: string
  readonly periodoAte: string
  readonly setor?: 'Público' | 'Privado' | undefined
}

function buscaDaRentabilidade(f: FiltrosDaRentabilidadeNaTela): URLSearchParams {
  const busca = new URLSearchParams({ periodo_de: f.periodoDe, periodo_ate: f.periodoAte })
  if (f.setor !== undefined) busca.set('setor', f.setor)
  return busca
}

export const rentabilidade = (f: FiltrosDaRentabilidadeNaTela, sinal?: AbortSignal) =>
  pedir<RespostaDaRentabilidade>(
    `/rentabilidade?${buscaDaRentabilidade(f).toString()}`,
    sinal === undefined ? {} : { sinal },
  )

/** DS-38 — leva os MESMOS filtros da tela, e o servidor registra o ato. */
export const exportarRentabilidade = (f: FiltrosDaRentabilidadeNaTela & { readonly q?: string }) => {
  const busca = buscaDaRentabilidade(f)
  busca.set('formato', 'csv')
  if (f.q !== undefined && f.q !== '') busca.set('q', f.q)
  return baixar(`/rentabilidade/exportar?${busca.toString()}`)
}

// --------------------------------------------------------- RH · 9.3 (telas 7.1/7.2)

export const colaboradores = (f: Partial<BuscaDeColaboradores> = {}, sinal?: AbortSignal) => {
  const busca = new URLSearchParams()
  if (f.q !== undefined && f.q !== '') busca.set('q', f.q)
  if (f.status !== undefined) busca.set('status', f.status)
  if (f.departamentoIds !== undefined && f.departamentoIds.length > 0) {
    busca.set('departamentoIds', f.departamentoIds.join(','))
  }
  if (f.tipoContratacaoId !== undefined) busca.set('tipoContratacaoId', f.tipoContratacaoId)
  busca.set('sort', f.sort ?? 'nome')
  busca.set('direcao', f.direcao ?? 'asc')
  busca.set('page', String(f.page ?? 1))
  busca.set('limit', String(f.limit ?? 10))
  return pedir<RespostaDaListaDeColaboradores>(
    `/colaboradores?${busca.toString()}`,
    sinal === undefined ? {} : { sinal },
  )
}

export const exportarColaboradores = (ids: readonly string[]) => {
  const busca = new URLSearchParams({ ids: ids.join(','), formato: 'csv' })
  return baixar(`/colaboradores/exportar?${busca.toString()}`)
}

export const colaboradorPorId = (id: string, sinal?: AbortSignal) =>
  pedir<RespostaDaFichaDoColaborador>(`/colaboradores/${id}`, sinal === undefined ? {} : { sinal })

export const contratosDoColaborador = (id: string, sinal?: AbortSignal) =>
  pedir<RespostaDosContratosRh>(`/colaboradores/${id}/contratos`, sinal === undefined ? {} : { sinal })

export const pagamentosDoColaborador = (id: string, sinal?: AbortSignal) =>
  pedir<RespostaDosPagamentosRh>(`/colaboradores/${id}/pagamentos`, sinal === undefined ? {} : { sinal })

export const criarColaboradorComContrato = (corpo: EntradaDeNovoColaboradorComContrato) =>
  pedir<{ id: string; contratoId: string; entityId: string }>('/colaboradores/com-contrato', {
    metodo: 'POST', corpo,
  })

export const editarColaborador = (id: string, corpo: EntradaDeEdicaoDoColaborador) =>
  pedir<{ id: string }>(`/colaboradores/${id}`, { metodo: 'PATCH', corpo })

export const desligarColaboradoresEmLote = (corpo: EntradaDeDesligamentoEmLote) =>
  pedir<{ id: string; ids: string[] }>('/colaboradores/desligar-lote', { metodo: 'POST', corpo })

export const reativarColaboradoresEmLote = (corpo: EntradaDeReativacaoEmLote) =>
  pedir<{ id: string; ids: string[] }>('/colaboradores/reativar-lote', { metodo: 'POST', corpo })

export const criarContratoRh = (colaboradorId: string, corpo: EntradaDeContratoRh) =>
  pedir<{ id: string }>(`/colaboradores/${colaboradorId}/contratos`, { metodo: 'POST', corpo })

export const atualizarCalendarioContratoRh = (
  contratoId: string,
  corpo: { diaPagamento: number; mesesAposCompetencia: 0 | 1 },
) => pedir<{ id: string }>(`/contratos/${contratoId}/calendario`, { metodo: 'PATCH', corpo })

export const criarAditivoRh = (contratoId: string, corpo: EntradaDeAditivoRh) =>
  pedir<{ id: string }>(`/contratos/${contratoId}/aditivo`, { metodo: 'POST', corpo })

/** PK-03: a correção cadastral — preenche a PJ que faltava, sem aditivo (DS-514). */
export const vincularEmpresaContratada = (contratoId: string, corpo: EntradaDeVinculoDeEmpresaContratada) =>
  pedir<{ id: string; versoes: number }>(`/contratos/${contratoId}/empresa-contratada`, { metodo: 'POST', corpo })

export const ajustarPagamentoRh = (pagamentoId: string, corpo: EntradaDeAjusteManualRh) =>
  pedir<{ id: string }>(`/pagamentos-rh/${pagamentoId}`, { metodo: 'PATCH', corpo })

export const registrarDocumentoRh = (pagamentoId: string, corpo: EntradaDeRegistroDeDocumentoRh) =>
  pedir<{ id: string }>(`/pagamentos-rh/${pagamentoId}/registrar-nf`, { metodo: 'POST', corpo })

export const reverterDocumentoRh = (pagamentoId: string) =>
  pedir<{ id: string }>(`/pagamentos-rh/${pagamentoId}/reverter-registro-nf`, { metodo: 'POST' })

export const enviarPagamentoRhParaAprovacao = (pagamentoId: string) =>
  pedir<{ id: string }>(`/pagamentos-rh/${pagamentoId}/enviar-aprovacao`, { metodo: 'POST' })

export const registrarLiquidacaoRh = (pagamentoId: string, corpo: EntradaDeLiquidacaoRh) =>
  pedir<{ id: string }>(`/pagamentos-rh/${pagamentoId}/registrar-pagamento`, { metodo: 'POST', corpo })

export const estornarLiquidacaoRh = (liquidacaoId: string) =>
  pedir<{ ok: true }>(`/liquidacoes-rh/${liquidacaoId}`, { metodo: 'DELETE' })

// --------------------------------------------------------- Tarefas globais · 9.4

export interface FiltrosDeTarefasNaTela {
  readonly estagioId?: string | undefined
  readonly responsavelId?: string | undefined
  readonly prioridade?: 'baixa' | 'média' | 'alta' | undefined
  readonly q?: string | undefined
  readonly alocada?: false | undefined
  readonly semana?: string | undefined
  readonly vinculoTipo?: 'empresa'|'contato'|'negocio'|'pedido_venda'|'campanha'|'fornecedor'|'colaborador'|'orcamento_versao' | undefined
  readonly vinculoId?: string | undefined
  readonly minhas?: true | undefined
  readonly pendentes?: true | undefined
  readonly sort?: 'prazo' | undefined
  /**
   * O recorte de período do quadro 9.1 — e ele governa SÓ a coluna que conclui.
   *
   * A regra é do servidor (`tarefas.controller.ts`: `NOT e.conclui OR concluida_em BETWEEN
   * …`): pendência não prescreve, então tarefa aberta fora da janela continua à vista. Os
   * dois andam juntos ou nenhum vale — meia janela seria um filtro que a tela acredita ter
   * pedido e que o servidor ignora.
   */
  readonly periodoDe?: string | undefined
  readonly periodoAte?: string | undefined
}

function buscaDeTarefas(f: FiltrosDeTarefasNaTela): string {
  const busca = new URLSearchParams()
  if (f.estagioId !== undefined) busca.set('estagio_id',f.estagioId)
  if (f.responsavelId !== undefined) busca.set('responsavel_id',f.responsavelId)
  if (f.prioridade !== undefined) busca.set('prioridade',f.prioridade)
  if (f.q !== undefined && f.q !== '') busca.set('q',f.q)
  if (f.alocada === false) busca.set('alocada','false')
  if (f.semana !== undefined) busca.set('semana',f.semana)
  if (f.vinculoTipo !== undefined) busca.set('vinculo_tipo',f.vinculoTipo)
  if (f.vinculoId !== undefined) busca.set('vinculo_id',f.vinculoId)
  if (f.minhas===true) busca.set('minhas','true')
  if (f.pendentes===true) busca.set('pendentes','true')
  if (f.sort!==undefined) busca.set('sort',f.sort)
  /* Os dois juntos, nunca um só: o servidor exige o par para aplicar o recorte. */
  if (f.periodoDe !== undefined && f.periodoAte !== undefined) {
    busca.set('periodo_de',f.periodoDe)
    busca.set('periodo_ate',f.periodoAte)
  }
  const serializada=busca.toString()
  return serializada === '' ? '/tarefas' : `/tarefas?${serializada}`
}

/*
  Os estágios do quadro vêm de `/tarefas/estagios` (DS-539), na forma plana e com a
  permissão de Tarefas.

  Em 31/08 este caminho era órfão (e933366c): a API não o declarava e o quadro dizia "Sem
  estágios configurados". O cliente foi então para `/catalogos/estagios-tarefa`, uma rota
  FIXA do TarefasController que sombreava o `/catalogos/:tipo` genérico e quebrou a 10.1.
  Hoje a API declara `/tarefas/estagios`, e duas guardas de `rotas-do-front.test.js` cobrem
  os dois defeitos: chamada sem rota, e rota fixa sobre a com parâmetro de outro controlador.
*/
export const estagiosDeTarefas = (sinal?:AbortSignal) =>
  pedir<{data:EstagioDaTarefa[]}>('/tarefas/estagios',sinal===undefined?{}:{sinal})
export const tarefas = (f:FiltrosDeTarefasNaTela={},sinal?:AbortSignal) =>
  pedir<{data:ResumoDaTarefa[]}>(buscaDeTarefas(f),sinal===undefined?{}:{sinal})
export const tarefaPorId = (id:string,sinal?:AbortSignal) =>
  pedir<Tarefa>(`/tarefas/${id}`,sinal===undefined?{}:{sinal})
export const criarTarefa = (corpo:EntradaDeTarefa) =>
  pedir<Tarefa>('/tarefas',{metodo:'POST',corpo})
export const editarTarefa = (id:string,corpo:EntradaDeEdicaoDeTarefa) =>
  pedir<Tarefa>(`/tarefas/${id}`,{metodo:'PATCH',corpo})
/**
 * Excluir é REVERSÍVEL — `deleted_at`, e não `DELETE FROM`.
 *
 * Por isso a tela oferece Desfazer, e por isso a restauração é uma rota e não um truque do
 * navegador: quem desfaz depois de recarregar a página continua desfazendo.
 */
export const excluirTarefa = (id: string) =>
  pedir<{ ok: true }>(`/tarefas/${id}`, { metodo: 'DELETE' })

export const restaurarTarefa = (id: string) =>
  pedir<Tarefa>(`/tarefas/${id}/restaurar`, { metodo: 'POST' })

export const moverTarefa = (id:string,estagioId:string) =>
  pedir<Tarefa>(`/tarefas/${id}/mover`,{metodo:'POST',corpo:{estagioId}})
export const vincularTarefa = (id:string,vinculos:readonly {tipo:'empresa'|'contato'|'negocio'|'pedido_venda'|'campanha'|'fornecedor'|'colaborador'|'orcamento_versao';id:string}[]) =>
  pedir<Tarefa>(`/tarefas/${id}/vinculos`,{metodo:'PUT',corpo:{vinculos}})
export const alocacoesDeTarefas = (semana:string,usuarioId?:string,sinal?:AbortSignal) => {
  const busca=new URLSearchParams({semana})
  if (usuarioId!==undefined) busca.set('usuario',usuarioId)
  return pedir<{data:AlocacaoDaTarefa[]}>(`/alocacoes?${busca.toString()}`,sinal===undefined?{}:{sinal})
}
export const alocarTarefa = (corpo:EntradaDeAlocacao) =>
  pedir<{id:string}>('/alocacoes',{metodo:'PUT',corpo})
export const desalocarTarefa = (id:string) => pedir<{ok:true}>(`/alocacoes/${id}`,{metodo:'DELETE'})

// ---------------------------------------------------- fornecedores · 6.6 (7.4)

export const fornecedores = (f: { q?: string; categoria?: string; limite?: number } = {}, sinal?: AbortSignal) => {
  const b = new URLSearchParams()
  if (f.q !== undefined && f.q !== '') b.set('q', f.q)
  if (f.categoria !== undefined && f.categoria !== '') b.set('categoria', f.categoria)
  // A página do seletor com busca (item 03 · DS-529); sem ela, o padrão do contrato (200).
  if (f.limite !== undefined) b.set('limit', String(f.limite))
  const busca = b.toString()
  return pedir<RespostaDeFornecedores>(
    busca === '' ? '/fornecedores' : `/fornecedores?${busca}`,
    sinal === undefined ? {} : { sinal },
  )
}

/**
 * `id` é o uuid da CAPACIDADE (`capacidade_fornecedor`), nunca o da Empresa — é a
 * mesma chave de `6.6?id=` e de `conta_pagar.fornecedor_capacidade_id`, e a única
 * que endereça também o fornecedor pessoa física.
 */
export const fornecedorPorId = (id: string, sinal?: AbortSignal) =>
  pedir<Fornecedor>(`/fornecedores/${id}`, sinal === undefined ? {} : { sinal })

/**
 * "Habilitar fornecedor" (D-138, DS-535): parte + capacidade + perfil num COMMIT só. O
 * corpo diz o estado (`criado`, `ja_habilitado`, `reativado`); a habilitação inativa sem
 * `reativar` é 409 `fornecedor.inativo`.
 */
export const habilitarFornecedor = (corpo: EntradaDeHabilitacaoDeFornecedor) =>
  pedir<ResultadoDaHabilitacao>('/fornecedores', { metodo: 'POST', corpo })

/** Termos da capacidade e o perfil por modo; `ativa: false` desativa (DS-535). */
export const editarFornecedor = (id: string, corpo: EntradaDeEdicaoDeFornecedor) =>
  pedir<{ id: string }>(`/fornecedores/${id}`, { metodo: 'PATCH', corpo })

/**
 * A busca do modal "Habilitar fornecedor": Empresas e Contatos do ambiente, pela rota do
 * módulo de fornecedores (sem exigir CRM · EI-04). Mínimo de 2 caracteres.
 */
export const partesParaHabilitar = (q: string, sinal?: AbortSignal) =>
  pedir<RespostaDePartesParaHabilitar>(
    `/fornecedores/partes?${new URLSearchParams({ q }).toString()}`,
    sinal === undefined ? {} : { sinal },
  )

/** Os perfis de pagamento de uma parte, para o "Usar perfil já cadastrado" do modal. */
export const perfisDaParte = (parteTipo: 'empresa' | 'contato', parteId: string, sinal?: AbortSignal) =>
  pedir<PerfilDaParte[]>(
    `/fornecedores/partes/${parteTipo}/${parteId}/perfis-pagamento`,
    sinal === undefined ? {} : { sinal },
  )

/** Os perfis da parte dona do fornecedor, para a edição do perfil pela 6.6. */
export const perfisDoFornecedor = (id: string, sinal?: AbortSignal) =>
  pedir<PerfilDaParte[]>(`/fornecedores/${id}/perfis-pagamento`, sinal === undefined ? {} : { sinal })

/**
 * O fornecedor da Nova despesa (6.4 · D-138): busca no servidor, 20 por vez, com o prazo e
 * a conta sugerida pela categoria; `id` resolve o valor que o seletor já tem (EI-05).
 */
export const fornecedoresDaDespesa = (f: { q?: string; id?: string }, sinal?: AbortSignal) => {
  const b = new URLSearchParams()
  if (f.q !== undefined && f.q !== '') b.set('q', f.q)
  if (f.id !== undefined) b.set('id', f.id)
  const busca = b.toString()
  return pedir<RespostaDeFornecedoresDaDespesa>(
    busca === '' ? '/contas-pagar/fornecedores' : `/contas-pagar/fornecedores?${busca}`,
    sinal === undefined ? {} : { sinal },
  )
}

/**
 * As exportações da fase B.3 — oito listas que não tinham.
 *
 * Todas seguem a convenção dos nove exportadores que já existiam: `?formato=csv` explícito
 * (a API recusa qualquer outro), o RECORTE da tela na mesma URL, e o ato registrado na
 * auditoria do lado do servidor.
 *
 * Cada uma reusa o montador de busca da PRÓPRIA lista — `comoQuery`, `queryDeNegocios`,
 * `queryDePedidos`, `queryDePrevisao`. Montar a query de novo aqui não seria só repetição:
 * as telas falam em `periodoDe` e a API em `periodo_de`, e um segundo tradutor erraria o
 * nome de um parâmetro em silêncio — o arquivo sairia com a base inteira e ninguém veria,
 * porque um CSV com linhas demais parece um CSV que funcionou.
 */
function comFormatoCsv(query: string): string {
  return query === '' ? '?formato=csv' : `${query}&formato=csv`
}

export const exportarEmpresas = (filtro: FiltroDeEmpresas = {}, ids?: readonly string[]) =>
  baixar(`/empresas/exportar${comFormatoCsv(comoQuery(filtro))}${ids === undefined ? '' : '&ids=' + encodeURIComponent(ids.join(','))}`)

export const exportarNegocios = (filtro: FiltroDeNegocios = {}) =>
  baixar(`/negocios/exportar${comFormatoCsv(queryDeNegocios(filtro))}`)

export const exportarAtingimento = (eixo: string, filtro: FiltroDePrevisao = {}) => {
  const query = queryDePrevisao({ periodoDe: filtro.periodoDe, periodoAte: filtro.periodoAte })
  return baixar(`/metas/atingimento/exportar${comFormatoCsv(query)}&eixo=${eixo}`)
}

export const exportarPrevisao = (filtro: FiltroDePrevisao = {}) =>
  baixar(`/previsao/exportar${comFormatoCsv(queryDePrevisao(filtro))}`)

/** A fila NÃO responde a período — ela é posição (spec 05 §3, exceção 2). Sem filtros. */
export const exportarFilaDeFaturamento = () => baixar('/faturamento/fila/exportar?formato=csv')

export function exportarLivroDeNotas(
  recorte: { periodoDe?: string; periodoAte?: string; situacao?: string; q?: string },
) {
  const partes = new URLSearchParams({ formato: 'csv' })
  const por = (chave: string, valor?: string): void => {
    if (valor !== undefined && valor !== '') partes.set(chave, valor)
  }
  por('periodo_de', recorte.periodoDe)
  por('periodo_ate', recorte.periodoAte)
  por('situacao', recorte.situacao)
  por('q', recorte.q)
  return baixar(`/nfs/exportar?${partes.toString()}`)
}


export const encargosDoRecebivel=(id:string)=>pedir<EncargosDoRecebivel>(`/recebiveis/${id}/encargos`)
